import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Inventory2Outlined as Inventory2OutlinedIcon } from "../../../components/icons";
import { LocalShippingOutlined as LocalShippingOutlinedIcon, AssignmentTurnedInOutlined as AssignmentTurnedInOutlinedIcon, StraightenOutlined as StraightenOutlinedIcon, VisibilityOutlined as VisibilityOutlinedIcon } from "../../../components/icons";
import { PendingActionsOutlined as PendingActionsOutlinedIcon, AcUnitOutlined as AcUnitOutlinedIcon, CheckCircleOutlineOutlined as CheckCircleOutlineOutlinedIcon, AutorenewOutlined as AutorenewOutlinedIcon, Search as SearchIcon, ArrowBackOutlined as ArrowBackOutlinedIcon } from "../../../components/icons";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  completeMortuaryStage,
  completeMortuaryHygieneChecklist,
  createExternalParlourRelease,
  createFamilyDeliveryDispatch,
  createInternalTransferDispatch,
  downloadOperationalSignature,
  getMortuaryHygieneChecklists,
  getMortuaryHistory,
  getMortuaryRecord,
  getMortuaryRecords,
  getMortuaryCapacities,
  getMortuaryStorageUnits,
  getMortuaryTemperatureLogs,
  registerMortuaryIntake,
  recordMortuaryTemperatureLog,
  startMortuaryStage,
  updateMortuaryMeasurement,
  updateMortuaryStage,
  upsertMortuaryStorageUnit,
} from "../../../services/dashboard";
import { getCasePaymentStatus } from "../../../services/revenue";
import Loader from "../../../components/Loader";
import SignatureUploadField from "../../../components/SignatureUploadField";
import { useSessionStore } from "../../../store/useSessionStore";
import { useUserStore } from "../../../store/useUserStore";
import MortuaryHistoryTimeline from "./MortuaryHistoryTimeline";
import {
  formatOperationsStatus,
  ifasColors,
  OperationsDetailField,
  OperationsDetailGrid,
  OperationsEmptyState,
  OperationsPageShell,
  OperationsSectionHeader,
  operationsActionBarSx,
  operationsDetailPaperSx,
} from "../ifas/components/OperationsUI";
import {
  STANDARD_TONES,
  StandardListCardHead,
  StandardMetricCard,
  StandardPager,
  StatusDotChip,
  operationsStatusTone,
  standardClickableRowSx,
  standardListCardSx,
  standardListCellPrimarySx,
  standardListCellSecondarySx,
  standardListGhostBtnSx,
  standardListHeadCellSx,
  standardListScrollSx,
  standardOutlineBtnSx,
  standardRowActionBtnSx,
} from "../ifas/components/StandardUI";
import {
  canAccessIfasModule,
  hasIfasCapability,
} from "../../../utils/ifasAccess";

// Mortuary statuses are passed to StatusDotChip explicitly. The shared
// standardStatusTone inference cannot resolve them — INTAKE_REGISTERED and
// BODY_CONTROL_ACTIVE match none of its patterns and fall through to grey,
// which is why every status in this queue renders grey today.
// Must stay in step with the `size` the list fetch sends, or the pager
// computes a page count the server never had.
const QUEUE_PAGE_SIZE = 10;

const MORTUARY_STATUS_TONE = {
  PENDING_INTAKE: "amber",
  INTAKE_REGISTERED: "blue",
  BODY_CONTROL_ACTIVE: "blue",
  TRANSFER_IN_PROGRESS: "blue",
  RELEASED_TO_EXTERNAL_PARLOUR: "green",
  DELIVERED_TO_FAMILY: "green",
} as const;

const mortuaryStatusTone = (status?: string) =>
  MORTUARY_STATUS_TONE[status as keyof typeof MORTUARY_STATUS_TONE] ?? "grey";

const MORTUARY_STAGE_TONE = {
  NOT_STARTED: "grey",
  IN_PROGRESS: "blue",
  COMPLETED: "green",
} as const;

const mortuaryStageTone = (stage?: string) =>
  MORTUARY_STAGE_TONE[stage as keyof typeof MORTUARY_STAGE_TONE] ?? "grey";

// MortuaryStorageStatus. Amber is deliberately not used here: a shelf that is
// out of service is unusable, which is the one red state in this vocabulary.
const MORTUARY_STORAGE_TONE = {
  AVAILABLE: "green",
  OCCUPIED: "blue",
  OUT_OF_SERVICE: "red",
} as const;

const mortuaryStorageTone = (status?: string) =>
  MORTUARY_STORAGE_TONE[status as keyof typeof MORTUARY_STORAGE_TONE] ?? "grey";

// MortuaryChecklistStatus. OPEN is outstanding work, not a fault.
const MORTUARY_CHECKLIST_TONE = {
  OPEN: "amber",
  COMPLETED: "green",
} as const;

const mortuaryChecklistTone = (status?: string) =>
  MORTUARY_CHECKLIST_TONE[status as keyof typeof MORTUARY_CHECKLIST_TONE] ?? "grey";

// ─── Shared sx helpers (outside component for performance) ────────────────────

const intakeSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    fontSize: 14,
    backgroundColor: "#fff",
    minHeight: 52,

    "& fieldset": {
      borderColor: "#e5e7eb",
    },

    "&:hover fieldset": {
      borderColor: "#fb923c",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#f97316",
      borderWidth: "1.5px",
      boxShadow: "0 0 0 4px rgba(249,115,22,0.10)",
    },
  },

  "& .MuiInputLabel-root": {
    fontSize: 13,
    color: "#6b7280",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: "#f97316",
  },
};

const tagInnerSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    fontSize: 12.5,
    bgcolor: "#f9fafb",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f97316" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#f97316",
      boxShadow: "0 0 0 2px rgba(249,115,22,0.1)",
    },
    "&.Mui-focused": { bgcolor: "#fff" },
  },
  "& .MuiInputLabel-root": { display: "none" },
  "& .MuiInputBase-root legend": { display: "none" },
  "& .MuiOutlinedInput-notchedOutline": { top: 0 },
};

// ─── Section divider used across all forms ────────────────────────────────────

function SecLabel({ text }: { text: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 1.5,
        pb: 1,
        borderBottom: "1px solid #f1f5f9",
      }}
    >
      <Box
        sx={{ width: 3, height: 14, borderRadius: "2px", bgcolor: "#f97316", flexShrink: 0 }}
      />
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 700,
          color: "#94a3b8",
          textTransform: "uppercase",
          letterSpacing: "0.07em",
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STAGES = [
  { key: "ARRIVAL", label: "Arrival" },
  { key: "WASHING", label: "Washing" },
  { key: "CLOTHES", label: "Clothes" },
  { key: "DRESSING", label: "Dressing" },
  { key: "COFFINING", label: "Coffining" },
  { key: "DELIVERY", label: "Delivery" },
] as const;

const emptyCounts = {
  PENDING_INTAKE: 0,
  INTAKE_REGISTERED: 0,
  BODY_CONTROL_ACTIVE: 0,
  TRANSFER_IN_PROGRESS: 0,
  RELEASED_TO_EXTERNAL_PARLOUR: 0,
  DELIVERED_TO_FAMILY: 0,
};

const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format("DD MMM YYYY HH:mm") : "Not captured";

const formatBool = (value?: boolean | null) =>
  value === true ? "Yes" : value === false ? "No" : "Not captured";

const normalizeMortuaryStatus = (
  status?: string | null,
  intakeRegisteredAt?: string | null,
) => (intakeRegisteredAt ? status || "PENDING_INTAKE" : "PENDING_INTAKE");

const normalizeCurrentStage = (
  stage?: string | null,
  status?: string | null,
  intakeRegisteredAt?: string | null,
) => {
  if (stage) return stage;
  return normalizeMortuaryStatus(status, intakeRegisteredAt) === "PENDING_INTAKE"
    ? "AWAITING_INTAKE"
    : "ARRIVAL";
};

const normalizeName = (value?: string | null) =>
  (value || "").trim().replace(/\s+/g, " ").toUpperCase();

const storageOptionValue = (unit: any) =>
  `${unit?.fridgeNumber || ""}|||${unit?.shelfNumber || ""}`;

function stageStatusValue(detail: any, key: string) {
  if (!detail?.intakeRegisteredAt) return "NOT_STARTED";
  const statusMap: Record<string, string> = {
    ARRIVAL: detail?.arrivalStageStatus,
    WASHING: detail?.washingStageStatus,
    CLOTHES: detail?.clothesStageStatus,
    DRESSING: detail?.dressingStageStatus,
    COFFINING: detail?.coffiningStageStatus,
    DELIVERY: detail?.deliveryStageStatus,
  };
  return statusMap[key] || "NOT_STARTED";
}

const getStageStartBlockedReason = (record: any, stageKey: string) => {
  if (!record) return "";
  if (record?.coffiningStageStatus === "COMPLETED" && stageKey !== "DELIVERY") {
    return "After coffining, only delivery can continue.";
  }

  const stageIndex = STAGES.findIndex((item) => item.key === stageKey);
  if (stageIndex > 0) {
    const previousStageKey = STAGES[stageIndex - 1].key;
    if (stageStatusValue(record, previousStageKey) !== "COMPLETED") {
      return `${formatOperationsStatus(previousStageKey)} must be signed off first.`;
    }
  }

  if (stageKey === "CLOTHES" && !hasMortuaryMeasurementDetails(record)) {
    return "Body measurements must be captured during washing before clothes stage can begin.";
  }

  if (stageKey === "COFFINING" && !record?.coffinTypeConfirmedByLineManager) {
    return "Line manager coffin confirmation is still pending.";
  }

  if ((stageKey === "DRESSING" || stageKey === "COFFINING") && record?.deceasedFullName) {
    const deceasedName = normalizeName(record.deceasedFullName);
    const toeMatches = !record?.toeTagName || normalizeName(record.toeTagName) === deceasedName;
    const wristMatches = !record?.wristTagName || normalizeName(record.wristTagName) === deceasedName;
    if (!toeMatches || !wristMatches) {
      return "Toe and wrist tags must match the deceased details.";
    }
  }

  return "";
};

type DialogMode =
  | null
  | "INTAKE"
  | "MEASUREMENT"
  | "STAGE_COMPLETE"
  | "STAGE_VIEW"
  | "CREATE_INTERNAL_TRANSFER"
  | "CREATE_EXTERNAL_RELEASE"
  | "CREATE_FAMILY_DELIVERY"
  | "TEMPERATURE_LOG"
  | "STORAGE_UNIT"
  | "HYGIENE_CHECKLIST";

type MortuaryPortalProps = {
  mortuaryRecordId?: string;
  onSelectMortuaryRecord?: (id: string) => void;
  onBack?: () => void;
  onBodyDispatchClicked?: () => void;
};

// ─── Dialog title helper ──────────────────────────────────────────────────────

function getDialogTitle(dialogMode: DialogMode, stageKey: string) {
  const plainTitles: Record<string, string> = {
    INTAKE: "Register Mortuary Intake",
    MEASUREMENT: "Update Measurement",
    STAGE_COMPLETE: `Complete ${formatOperationsStatus(stageKey)} Stage`,
    STAGE_VIEW: `${formatOperationsStatus(stageKey)} Stage Details`,
    CREATE_INTERNAL_TRANSFER: "Prepare Internal Transfer",
    CREATE_EXTERNAL_RELEASE: "Prepare External Parlour Release",
    CREATE_FAMILY_DELIVERY: "Prepare Family Delivery",
    TEMPERATURE_LOG: "Log Fridge Temperature",
    STORAGE_UNIT: "Shelf Master Setup",
    HYGIENE_CHECKLIST: "Daily Hygiene Checklist",
  };

  return plainTitles[dialogMode ?? ""] ?? "";
}

function getDialogSubmitLabel(dialogMode: DialogMode, stageKey: string, isStageViewEditable = false) {
  const labels: Record<string, string> = {
    INTAKE: "Register Intake",
    MEASUREMENT: "Save Measurement",
    STAGE_COMPLETE: `Complete ${formatOperationsStatus(stageKey)}`,
    CREATE_INTERNAL_TRANSFER: "Prepare Transfer",
    CREATE_EXTERNAL_RELEASE: "Prepare Release",
    CREATE_FAMILY_DELIVERY: "Prepare Delivery",
    TEMPERATURE_LOG: "Save Reading",
    STORAGE_UNIT: "Save Shelf",
    HYGIENE_CHECKLIST: "Submit Checklist",
  };

  if (dialogMode === "STAGE_VIEW" && isStageViewEditable) {
    return "Save Changes";
  }

  return labels[dialogMode ?? ""] ?? "Save Changes";
}

function getDialogSubtitle(selectedRecord: any) {
  return [selectedRecord?.bodyRemovalNumber, selectedRecord?.deceasedFullName, selectedRecord?.mortuaryName]
    .filter(Boolean)
    .join(" • ");
}

function getDialogIcon(dialogMode: DialogMode) {
  if (dialogMode === "MEASUREMENT") {
    return <StraightenOutlinedIcon fontSize="small" />;
  }

  if (dialogMode === "STAGE_COMPLETE" || dialogMode === "STAGE_VIEW") {
    return <AssignmentTurnedInOutlinedIcon fontSize="small" />;
  }

  if (
    dialogMode === "CREATE_INTERNAL_TRANSFER" ||
    dialogMode === "CREATE_EXTERNAL_RELEASE" ||
    dialogMode === "CREATE_FAMILY_DELIVERY"
  ) {
    return <LocalShippingOutlinedIcon fontSize="small" />;
  }

  return <Inventory2OutlinedIcon fontSize="small" />;
}

const stageSummaryFieldMap: Record<
  string,
  {
    startedAt: string;
    supervisorName: string;
    supervisorSignature: string;
  }
> = {
  ARRIVAL: {
    startedAt: "arrivalStageStartedAt",
    supervisorName: "arrivalStageSupervisorName",
    supervisorSignature: "arrivalStageSupervisorSignature",
  },
  WASHING: {
    startedAt: "washingStageStartedAt",
    supervisorName: "washingSupervisorName",
    supervisorSignature: "washingSupervisorSignature",
  },
  CLOTHES: {
    startedAt: "clothesStageStartedAt",
    supervisorName: "clothesSupervisorName",
    supervisorSignature: "clothesSupervisorSignature",
  },
  DRESSING: {
    startedAt: "dressingStageStartedAt",
    supervisorName: "dressingSupervisorName",
    supervisorSignature: "dressingSupervisorSignature",
  },
  COFFINING: {
    startedAt: "coffiningStageStartedAt",
    supervisorName: "coffiningSupervisorName",
    supervisorSignature: "coffiningSupervisorSignature",
  },
  DELIVERY: {
    startedAt: "deliveryStageStartedAt",
    supervisorName: "deliverySupervisorName",
    supervisorSignature: "deliverySupervisorSignature",
  },
};

const deriveStoredFileName = (value?: string | null) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "Not uploaded";
  const parts = normalized.split("/");
  return parts[parts.length - 1] || normalized;
};

const getStageSummaryValue = (record: any, currentStageKey: string, field: "startedAt" | "supervisorName" | "supervisorSignature") => {
  const mapping = stageSummaryFieldMap[currentStageKey];
  if (!mapping) return "";
  return record?.[mapping[field]] || "";
};

const hasMortuaryMeasurementDetails = (record: any) =>
  Boolean(
    record?.measurementCasketType ||
    record?.measurementLengthCm ||
    record?.measurementWidthCm ||
    record?.measurementHeightCm ||
    record?.measuredSizeCategory,
  );

// ─── Main component ───────────────────────────────────────────────────────────

export default function MortuaryPortal({
  mortuaryRecordId,
  onSelectMortuaryRecord,
  onBack,
  onBodyDispatchClicked,
}: MortuaryPortalProps) {
  const { userName, userRole } = useSessionStore();
  const storedRole = useUserStore((state) => state.role);
  const effectiveRole = userRole || storedRole;
  const canViewMortuary = canAccessIfasModule(effectiveRole, "mortuary");
  const canAssistMortuary = hasIfasCapability(effectiveRole, "mortuaryAssist");
  const canApproveMortuary = hasIfasCapability(effectiveRole, "mortuaryApprove");
  const canOperateMortuaryStage = canApproveMortuary || canAssistMortuary;
  const isDetailsView = Boolean(mortuaryRecordId);

  const [records, setRecords] = useState<any[]>([]);
  const [statusCounts, setStatusCounts] = useState<any>(emptyCounts);
  const [queueTotal, setQueueTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [searchText, setSearchText] = useState("");
  const [mortuaryFilter, setMortuaryFilter] = useState("");
  const [capacityProvinceFilter, setCapacityProvinceFilter] = useState("");
  const [capacityRegionFilter, setCapacityRegionFilter] = useState("");
  const [capacitySearchText, setCapacitySearchText] = useState("");
  const [mortuaryCapacities, setMortuaryCapacities] = useState<any[]>([]);
  const [capacityLoading, setCapacityLoading] = useState(false);
  // getMortuaryCapacities has no page/size params (unlike getMortuaryRecords),
  // so the registers table paginates client-side over the already-fetched list.
  const [capacityPage, setCapacityPage] = useState(0);
  const [manageMenuAnchor, setManageMenuAnchor] = useState<{ el: HTMLElement; mortuaryName: string } | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  // Finance clearance for the open record's case (SOP §12.1 payment gate).
  const [paymentStatus, setPaymentStatus] = useState<any>(null);
  const [financeBlockMessage, setFinanceBlockMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [stageKey, setStageKey] = useState<string>("");
  const [stageDialogReadOnly, setStageDialogReadOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stageActionLoadingKey, setStageActionLoadingKey] = useState("");
  const [formState, setFormState] = useState<any>({});
  const [temperatureLogs, setTemperatureLogs] = useState<any[]>([]);
  const [storageUnits, setStorageUnits] = useState<any[]>([]);
  const [hygieneChecklists, setHygieneChecklists] = useState<any[]>([]);
  const [complianceLoading, setComplianceLoading] = useState(false);

  const resetForm = useCallback(() => {
    setFormState({});
    setDialogMode(null);
    setStageKey("");
    setStageDialogReadOnly(false);
  }, []);

  const updateField = (field: string, value: any) => {
    setFormState((prev: any) => ({ ...prev, [field]: value }));
  };

  const previewSignatureFile = async (fileKey?: string | null) => {
    if (!fileKey) {
      toast.info("No signature file uploaded yet");
      return;
    }

    try {
      const response = await downloadOperationalSignature(fileKey);
      if (response?.status !== 200 || !response?.data) {
        toast.error(response?.data?.message || "Unable to open signature file");
        return;
      }

      const blob = new Blob([response.data], {
        type:
          response.headers?.["content-type"] ||
          response.headers?.["Content-Type"] ||
          "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (error) {
      console.error("Unable to preview signature file", error);
      toast.error("Unable to open signature file");
    }
  };

  const loadRecords = useCallback(async () => {
    setLoading(true);
    const response = await getMortuaryRecords({
      status: selectedStatus || undefined,
      mortuaryName: mortuaryFilter || undefined,
      searchText: searchText || undefined,
      page,
      size: QUEUE_PAGE_SIZE,
    });
    if (response?.status === 200) {
      const payload = response.data || {};
      setRecords(payload.records || []);
      setStatusCounts({ ...emptyCounts, ...(payload.statusCounts || {}) });
      setQueueTotal(Number(payload.totalCount || 0));
    } else {
      toast.error(response?.data?.message || "Failed to load mortuary records");
    }
    setLoading(false);
  }, [selectedStatus, mortuaryFilter, searchText, page]);

  const loadMortuaryCapacities = useCallback(async () => {
    setCapacityLoading(true);
    const response = await getMortuaryCapacities({
      province: capacityProvinceFilter || undefined,
      region: capacityRegionFilter || undefined,
      mortuaryName: capacitySearchText || undefined,
    });
    if (response?.status === 200) {
      setMortuaryCapacities(response.data || []);
    } else {
      toast.error(response?.data?.message || "Failed to load mortuary capacity");
    }
    setCapacityLoading(false);
  }, [capacityProvinceFilter, capacityRegionFilter, capacitySearchText]);

  const loadRecord = useCallback(async (recordId: string) => {
    if (!recordId) return;
    setDetailLoading(true);
    const response = await getMortuaryRecord(recordId);
    if (response?.status === 200) {
      setSelectedRecord(response.data);
      setFinanceBlockMessage(null);
      const caseNumber = response.data?.caseNumber;
      if (caseNumber) {
        const gate = await getCasePaymentStatus(caseNumber);
        setPaymentStatus(gate?.status === 200 ? gate.data : null);
      } else {
        setPaymentStatus(null);
      }
    } else {
      toast.error(response?.data?.message || "Failed to load mortuary details");
    }
    setDetailLoading(false);
  }, []);

  const loadHistory = useCallback(async (recordId: string) => {
    if (!recordId) return;
    setHistoryLoading(true);
    const response = await getMortuaryHistory(recordId);
    if (response?.status === 200) {
      setHistoryData(response.data || []);
    } else {
      toast.error(response?.data?.message || "Failed to load mortuary history");
    }
    setHistoryLoading(false);
  }, []);

  const loadCompliance = useCallback(async (mortuaryName?: string) => {
    if (!mortuaryName) return;
    setComplianceLoading(true);
    const [temperatureResponse, storageResponse, hygieneResponse] = await Promise.all([
      getMortuaryTemperatureLogs(mortuaryName),
      getMortuaryStorageUnits(mortuaryName),
      getMortuaryHygieneChecklists(mortuaryName),
    ]);
    if (temperatureResponse?.status === 200) setTemperatureLogs(temperatureResponse.data || []);
    if (storageResponse?.status === 200) setStorageUnits(storageResponse.data || []);
    if (hygieneResponse?.status === 200) setHygieneChecklists(hygieneResponse.data || []);
    setComplianceLoading(false);
  }, []);

  useEffect(() => {
    if (!isDetailsView) loadRecords();
  }, [isDetailsView, loadRecords]);

  useEffect(() => {
    loadMortuaryCapacities();
  }, [loadMortuaryCapacities]);

  useEffect(() => {
    if (isDetailsView && mortuaryRecordId) {
      setShowHistory(false);
      setHistoryData([]);
      loadRecord(mortuaryRecordId);
    } else if (!isDetailsView) {
      setSelectedRecord(null);
      setPaymentStatus(null);
      setFinanceBlockMessage(null);
      setShowHistory(false);
      setHistoryData([]);
    }
  }, [isDetailsView, mortuaryRecordId, loadRecord]);

  useEffect(() => {
    if (selectedRecord?.mortuaryName) {
      loadCompliance(selectedRecord.mortuaryName);
    }
  }, [selectedRecord?.mortuaryName, loadCompliance]);

  const refreshCurrentView = async () => {
    if (showHistory && mortuaryRecordId) {
      await loadHistory(mortuaryRecordId);
      return;
    }
    if (isDetailsView && mortuaryRecordId) await loadRecord(mortuaryRecordId);
    else await loadRecords();
  };

  const openHistory = async () => {
    if (!mortuaryRecordId) return;
    setShowHistory(true);
    await loadHistory(mortuaryRecordId);
  };

  const openTemperatureLog = (mortuaryName = "", fridgeNumber = "FRIDGE-1") => {
    if (!canApproveMortuary) {
      toast.error("Your role cannot log mortuary temperature.");
      return;
    }
    setStageDialogReadOnly(false);
    setFormState({
      mortuaryName: mortuaryName || selectedRecord?.mortuaryName || mortuaryFilter || "",
      fridgeNumber: fridgeNumber || selectedRecord?.fridgeNumber || "FRIDGE-1",
      readingDate: dayjs().format("YYYY-MM-DD"),
      shift: dayjs().hour() < 12 ? "AM" : "PM",
      temperatureCelsius: "",
      normalMinCelsius: -5,
      normalMaxCelsius: 5,
      correctiveAction: "",
      loggedBy: userName || "",
    });
    setDialogMode("TEMPERATURE_LOG");
  };

  const openHygieneChecklist = (mortuaryName = "") => {
    if (!canApproveMortuary) {
      toast.error("Your role cannot complete mortuary hygiene checks.");
      return;
    }
    setStageDialogReadOnly(false);
    setFormState({
      mortuaryName: mortuaryName || selectedRecord?.mortuaryName || mortuaryFilter || "",
      checklistDate: dayjs().format("YYYY-MM-DD"),
      shift: dayjs().hour() < 12 ? "AM" : "PM",
      protectiveClothingWorn: true,
      noEatingDrinkingSmoking: true,
      flyControlApplied: true,
      ventilationAndLightingOk: true,
      wasteBinsClosedAndEmptied: true,
      instrumentsCleanDryStored: true,
      entrancesClosed: true,
      coldRoomAndTraysClean: true,
      refrigerationDoorsSecure: true,
      floorsWallsSinksDisinfected: true,
      coffinsCasketsDusted: true,
      airFreshenersUsed: true,
      laundryRosterFollowed: true,
      handsWashedWithDisinfectant: true,
      showerCompletedEndOfDay: true,
      uvLightManaged: true,
      exceptions: "",
      completedBy: userName || "",
      supervisorName: userName || "",
      supervisorSignature: "",
    });
    setDialogMode("HYGIENE_CHECKLIST");
  };

  const openStorageSetup = (mortuaryName = "") => {
    if (!canApproveMortuary) {
      toast.error("Your role cannot maintain mortuary shelves.");
      return;
    }
    setStageDialogReadOnly(false);
    setFormState({
      mortuaryName: mortuaryName || selectedRecord?.mortuaryName || mortuaryFilter || "",
      fridgeNumber: "FRIDGE-1",
      shelfNumber: "",
      status: "AVAILABLE",
      notes: "",
    });
    setDialogMode("STORAGE_UNIT");
  };

  const openDialog = (mode: DialogMode, nextStageKey = "") => {
    const baseActor = userName || "";

    if (mode === "INTAKE") {
      setStageDialogReadOnly(false);
      setFormState({
        intakeRegisteredBy: baseActor,
        mortuaryName: selectedRecord?.mortuaryName || "",
        registerNumber: selectedRecord?.registerNumber || "",
        fridgeNumber: selectedRecord?.fridgeNumber || "FRIDGE-1",
        shelfNumber: selectedRecord?.shelfNumber || "",
        toeTagName: selectedRecord?.deceasedFullName || "",
        toeTagNumber: selectedRecord?.toeTagNumber || "",
        wristTagName: selectedRecord?.deceasedFullName || "",
        wristTagNumber: selectedRecord?.wristTagNumber || "",
        belongingsBagTagName: selectedRecord?.deceasedFullName || "",
        belongingsBagTagNumber: selectedRecord?.belongingsBagTagNumber || "",
        bodyConditionAtArrival: selectedRecord?.bodyConditionAtArrival || "",
        intakeNotes: selectedRecord?.intakeNotes || "",
      });
    } else if (mode === "MEASUREMENT") {
      setStageDialogReadOnly(false);
      setFormState({
        casketType: selectedRecord?.measurementCasketType || "",
        measurementLengthCm: selectedRecord?.measurementLengthCm || "",
        measurementWidthCm: selectedRecord?.measurementWidthCm || "",
        measurementHeightCm: selectedRecord?.measurementHeightCm || "",
        measuringEmployeeNames: selectedRecord?.measuringEmployeeNames || baseActor,
        measuringDesignation: selectedRecord?.measuringDesignation || "Mortuary Supervisor",
        measuringSignature: selectedRecord?.measuringSignature || "",
        measurementDate: selectedRecord?.measurementDate || dayjs().format("YYYY-MM-DD"),
        coffinTypeConfirmedByLineManager: selectedRecord?.coffinTypeConfirmedByLineManager || false,
        coffinTypeConfirmedBy: selectedRecord?.coffinTypeConfirmedBy || "",
        coffinTypeConfirmedAt: selectedRecord?.coffinTypeConfirmedAt || dayjs().format("YYYY-MM-DDTHH:mm"),
      });
    } else if (mode === "STAGE_COMPLETE" || mode === "STAGE_VIEW") {
      setStageKey(nextStageKey);
      setStageDialogReadOnly(mode === "STAGE_VIEW");
      setFormState({
        performedBy: baseActor,
        supervisorName:
          getStageSummaryValue(selectedRecord, nextStageKey, "supervisorName") || baseActor,
        supervisorSignature:
          getStageSummaryValue(selectedRecord, nextStageKey, "supervisorSignature") || "",
        washingGa1: selectedRecord?.washingGa1 || "",
        washingGa2: selectedRecord?.washingGa2 || "",
        clothesReceivedDate: selectedRecord?.clothesReceivedDate || dayjs().format("YYYY-MM-DD"),
        clothesTagsDone: Boolean(selectedRecord?.clothesTagsDone),
        clothesInsideTagDone: Boolean(selectedRecord?.clothesInsideTagDone ?? selectedRecord?.clothesTagsDone),
        clothesOutsideTagDone: Boolean(selectedRecord?.clothesOutsideTagDone ?? selectedRecord?.clothesTagsDone),
        clothesSpecialRequests: selectedRecord?.clothesSpecialRequests || "",
        clothesTopDescription: selectedRecord?.clothesTopDescription || "",
        clothesBottomDescription: selectedRecord?.clothesBottomDescription || "",
        clothesUnderneathDescription: selectedRecord?.clothesUnderneathDescription || "",
        dressingGa1: selectedRecord?.dressingGa1 || "",
        dressingGa2: selectedRecord?.dressingGa2 || "",
        dressingTagVerified: Boolean(selectedRecord?.dressingTagVerified),
        coffiningGa1: selectedRecord?.coffiningGa1 || "",
        coffiningGa2: selectedRecord?.coffiningGa2 || "",
        coffinConfirmed: Boolean(selectedRecord?.coffinConfirmed),
        deliveryClothesChecked: Boolean(selectedRecord?.deliveryClothesChecked),
        deliveryJewelleryChecked: Boolean(selectedRecord?.deliveryJewelleryChecked),
        deliveryCellphoneChecked: Boolean(selectedRecord?.deliveryCellphoneChecked),
        deliveryUndertakerName: selectedRecord?.deliveryUndertakerName || "",
      });
    } else if (mode === "CREATE_INTERNAL_TRANSFER") {
      setStageDialogReadOnly(false);
      setFormState({
        mortuaryRecordId: selectedRecord?.id,
        createdBy: baseActor,
        funeralFileNumber: selectedRecord?.funeralFileNumber || "",
        familyRepresentativeName: selectedRecord?.familyRepresentativeName || "",
        familyRepresentativeIdNumber: selectedRecord?.familyRepresentativeIdNumber || "",
        relationshipToDeceased: selectedRecord?.relationshipToDeceased || "",
        transferDateTime: dayjs().format("YYYY-MM-DDTHH:mm"),
        departureMortuary: selectedRecord?.mortuaryName || "",
        receivingMortuary: "",
        familyViewedConfirmed: false,
        clothesBelongingsHandedOver: false,
        clothesTransferred: "NA",
        bodyInBadState: false,
        operationsManagerEscalated: false,
        bothBranchesAgreed: false,
        transferVehicleRegistration: "",
        transferUndertakerName: "",
        dispatchingPersonnelName: "",
        mileageOut: "",
        notes: "",
      });
    } else if (mode === "CREATE_EXTERNAL_RELEASE") {
      setStageDialogReadOnly(false);
      setFormState({
        mortuaryRecordId: selectedRecord?.id,
        createdBy: baseActor,
        funeralFileNumber: selectedRecord?.funeralFileNumber || "",
        familyRepresentativeName: selectedRecord?.familyRepresentativeName || "",
        familyRepresentativeIdNumber: selectedRecord?.familyRepresentativeIdNumber || "",
        relationshipToDeceased: selectedRecord?.relationshipToDeceased || "",
        branchName: "",
        externalReleaseDateTime: dayjs().format("YYYY-MM-DDTHH:mm"),
        sapfaRateAmount: "",
        amountPaid: "",
        reasonForTakingBody: "",
        parlourName: "",
        parlourContact: "",
        parlourDriverName: "",
        parlourDriverContact: "",
        deathCertificateVerified: false,
        deceasedIdVerified: false,
        informantIdVerified: false,
        dha1663Verified: false,
        parlourDriverIdVerified: false,
        federationCertificateVerified: false,
        invoiceVerified: false,
        proofOfPaymentVerified: false,
        financePaymentConfirmed: false,
        financeConfirmedBy: "",
        financeConfirmedAt: "",
        branchManagerOverride: false,
        branchManagerOverrideBy: "",
        branchManagerOverrideReason: "",
        parlourDriverIdNumber: "",
        parlourFederationCertificateNumber: "",
        parlourRepresentativeSignature: "",
        parlourSignatureDate: dayjs().format("YYYY-MM-DD"),
        parlourStampImage: "",
        claimantName: selectedRecord?.familyRepresentativeName || "",
        claimantIdNumber: selectedRecord?.familyRepresentativeIdNumber || "",
        claimantDeclarationAccepted: false,
        clothesWithBody: "NA",
        dispatchingMortuarySignature: "",
        dispatchingMortuarySignOffBy: baseActor,
        dispatchingMortuarySignOffDate: dayjs().format("YYYY-MM-DD"),
        claimantDeclarationSignature: "",
        claimantDeclarationDate: dayjs().format("YYYY-MM-DD"),
      });
    } else if (mode === "CREATE_FAMILY_DELIVERY") {
      setStageDialogReadOnly(false);
      setFormState({
        mortuaryRecordId: selectedRecord?.id,
        createdBy: baseActor,
        funeralFileNumber: selectedRecord?.funeralFileNumber || "",
        familyRepresentativeName: selectedRecord?.familyRepresentativeName || "",
        familyRepresentativeIdNumber: selectedRecord?.familyRepresentativeIdNumber || "",
        relationshipToDeceased: selectedRecord?.relationshipToDeceased || "",
        familyDeliveryDateTime: dayjs().format("YYYY-MM-DDTHH:mm"),
        deliveryAddress: "",
        familyViewingConfirmed: false,
        bodyReadinessConfirmed: false,
        handlesExplained: false,
        viaAddress: "",
        clothesReturned: false,
        cellphoneJewelleryReturned: false,
        casketType: selectedRecord?.measurementCasketType || "",
        casketColour: "",
        coffinSprayConfirmed: false,
        photoProgrammeConfirmed: false,
        crossPlaqueConfirmed: false,
        undertakerName: "",
        vehicleRegistration: "",
        familyDeliveryMileageOut: "",
        recipientName: "",
        recipientIdNumber: "",
        recipientRelationship: "",
        recipientSignature: "",
        icebolethuRepresentativeSignature: "",
        originalDeathCertificateVerified: false,
        beneficiaryIdVerified: false,
        personalEffectsSignedOff: false,
        notes: "",
      });
    } else if (mode === "TEMPERATURE_LOG") {
      setStageDialogReadOnly(false);
      setFormState({
        mortuaryName: selectedRecord?.mortuaryName || mortuaryFilter || "",
        fridgeNumber: selectedRecord?.fridgeNumber || "FRIDGE-1",
        readingDate: dayjs().format("YYYY-MM-DD"),
        shift: dayjs().hour() < 12 ? "AM" : "PM",
        temperatureCelsius: "",
        normalMinCelsius: -5,
        normalMaxCelsius: 5,
        correctiveAction: "",
        loggedBy: baseActor,
      });
    } else if (mode === "STORAGE_UNIT") {
      setStageDialogReadOnly(false);
      setFormState({
        mortuaryName: selectedRecord?.mortuaryName || mortuaryFilter || "",
        fridgeNumber: selectedRecord?.fridgeNumber || "FRIDGE-1",
        shelfNumber: "",
        status: "AVAILABLE",
        notes: "",
      });
    } else if (mode === "HYGIENE_CHECKLIST") {
      setStageDialogReadOnly(false);
      setFormState({
        mortuaryName: selectedRecord?.mortuaryName || mortuaryFilter || "",
        checklistDate: dayjs().format("YYYY-MM-DD"),
        shift: dayjs().hour() < 12 ? "AM" : "PM",
        protectiveClothingWorn: true,
        noEatingDrinkingSmoking: true,
        flyControlApplied: true,
        ventilationAndLightingOk: true,
        wasteBinsClosedAndEmptied: true,
        instrumentsCleanDryStored: true,
        entrancesClosed: true,
        coldRoomAndTraysClean: true,
        refrigerationDoorsSecure: true,
        floorsWallsSinksDisinfected: true,
        coffinsCasketsDusted: true,
        airFreshenersUsed: true,
        laundryRosterFollowed: true,
        handsWashedWithDisinfectant: true,
        showerCompletedEndOfDay: true,
        uvLightManaged: true,
        exceptions: "",
        completedBy: baseActor,
        supervisorName: baseActor,
        supervisorSignature: "",
      });
    }

    setDialogMode(mode);
  };

  const handleStartStage = async (nextStageKey: string) => {
    if (!selectedRecord) return;
    if (!canOperateMortuaryStage) {
      toast.error("Your role cannot start mortuary stages.");
      return;
    }

    const blockedReason = getStageStartBlockedReason(selectedRecord, nextStageKey);
    if (blockedReason) {
      toast.error(blockedReason);
      return;
    }

    setStageActionLoadingKey(nextStageKey);
    const response = await startMortuaryStage(selectedRecord.id, nextStageKey, {
      performedBy: userName || "System User",
    });

    if (response?.status === 200) {
      setFinanceBlockMessage(null);
      toast.success(response.data?.message || `${formatOperationsStatus(nextStageKey)} stage started successfully`);
      await refreshCurrentView();
    } else {
      const message = response?.data?.message || "Failed to start stage";
      // The SOP §12.1 refusal is a compliance hard-stop with an override path —
      // it must stay on screen, not vanish with a toast.
      if (/SOP\s*§?12\.1|blocked:/i.test(message)) {
        setFinanceBlockMessage(message);
      }
      toast.error(message);
    }

    setStageActionLoadingKey("");
  };

  const handleEditStage = () => {
    if (!canOperateMortuaryStage) {
      toast.error("Your role cannot edit mortuary stages.");
      return;
    }
    setStageDialogReadOnly(false);
  };

  const submitDialog = async () => {
    if (!selectedRecord && !["STORAGE_UNIT", "TEMPERATURE_LOG", "HYGIENE_CHECKLIST"].includes(dialogMode || "")) return;
    const isStageAction = dialogMode === "STAGE_COMPLETE";
    if (isStageAction ? !canOperateMortuaryStage : !canApproveMortuary) {
      toast.error("Your role cannot submit mortuary changes.");
      return;
    }
    setSaving(true);
    let response: any;

    if (dialogMode === "INTAKE") {
      response = await registerMortuaryIntake(selectedRecord.bodyRemovalNumber, formState);
    } else if (dialogMode === "MEASUREMENT") {
      response = await updateMortuaryMeasurement(selectedRecord.id, {
        ...formState,
        measurementLengthCm: formState.measurementLengthCm === "" ? null : Number(formState.measurementLengthCm),
        measurementWidthCm: formState.measurementWidthCm === "" ? null : Number(formState.measurementWidthCm),
        measurementHeightCm: formState.measurementHeightCm === "" ? null : Number(formState.measurementHeightCm),
      });
    } else if (dialogMode === "STAGE_COMPLETE") {
      response = await completeMortuaryStage(selectedRecord.id, stageKey, {
        ...formState,
        performedBy:
          formState.performedBy ||
          userName ||
          formState.supervisorName ||
          "System User",
      });
    } else if (dialogMode === "STAGE_VIEW" && !stageDialogReadOnly) {
      response = await updateMortuaryStage(selectedRecord.id, stageKey, {
        ...formState,
        performedBy:
          formState.performedBy ||
          userName ||
          formState.supervisorName ||
          "System User",
      });
    } else if (dialogMode === "CREATE_INTERNAL_TRANSFER") {
      response = await createInternalTransferDispatch({
        ...formState,
        mileageOut: formState.mileageOut === "" ? null : Number(formState.mileageOut),
        mileageIn: formState.mileageIn === "" ? null : Number(formState.mileageIn),
      });
    } else if (dialogMode === "CREATE_EXTERNAL_RELEASE") {
      response = await createExternalParlourRelease({
        ...formState,
        sapfaRateAmount: formState.sapfaRateAmount === "" ? null : Number(formState.sapfaRateAmount),
        amountPaid: formState.amountPaid === "" ? null : Number(formState.amountPaid),
      });
    } else if (dialogMode === "CREATE_FAMILY_DELIVERY") {
      response = await createFamilyDeliveryDispatch({
        ...formState,
        familyDeliveryMileageOut: formState.familyDeliveryMileageOut === "" ? null : Number(formState.familyDeliveryMileageOut),
      });
    } else if (dialogMode === "TEMPERATURE_LOG") {
      response = await recordMortuaryTemperatureLog({
        ...formState,
        temperatureCelsius: formState.temperatureCelsius === "" ? null : Number(formState.temperatureCelsius),
        normalMinCelsius: formState.normalMinCelsius === "" ? null : Number(formState.normalMinCelsius),
        normalMaxCelsius: formState.normalMaxCelsius === "" ? null : Number(formState.normalMaxCelsius),
      });
    } else if (dialogMode === "STORAGE_UNIT") {
      response = await upsertMortuaryStorageUnit(formState);
    } else if (dialogMode === "HYGIENE_CHECKLIST") {
      response = await completeMortuaryHygieneChecklist(formState);
    }

    if (response?.status === 200) {
      toast.success(response.data?.message || "Action completed successfully");
      const complianceMortuaryName = formState.mortuaryName || selectedRecord?.mortuaryName;
      resetForm();
      await refreshCurrentView();
      await loadCompliance(complianceMortuaryName);
      await loadMortuaryCapacities();
      if (
        dialogMode === "CREATE_INTERNAL_TRANSFER" &&
        onBodyDispatchClicked
      ) {
        onBodyDispatchClicked();
      }
    } else {
      toast.error(response?.data?.message || "Action failed");
    }
    setSaving(false);
  };

  const selectedMeta = useMemo(
    () => [
      {
        label: "Pending Intake",
        value: statusCounts.PENDING_INTAKE || 0,
        caption: "awaiting registration",
        tone: "amber" as const,
        icon: <PendingActionsOutlinedIcon />,
        // Single status — wired as a card filter (Samuel's ruling).
        filterValue: "PENDING_INTAKE",
      },
      {
        label: "In Body Control",
        value: (statusCounts.INTAKE_REGISTERED || 0) + (statusCounts.BODY_CONTROL_ACTIVE || 0),
        caption: "in mortuary care",
        tone: "blue" as const,
        icon: <AcUnitOutlinedIcon />,
        // Multi-status bucket (INTAKE_REGISTERED + BODY_CONTROL_ACTIVE) — the
        // controller takes one enum, so this card stays inert per Samuel's
        // ruling rather than fan out or apply only its first status.
        filterValue: undefined,
      },
      {
        label: "Transfer In Progress",
        value: statusCounts.TRANSFER_IN_PROGRESS || 0,
        caption: "moving between sites",
        tone: "blue" as const,
        icon: <LocalShippingOutlinedIcon />,
        filterValue: "TRANSFER_IN_PROGRESS",
      },
      {
        label: "Released / Delivered",
        value: (statusCounts.RELEASED_TO_EXTERNAL_PARLOUR || 0) + (statusCounts.DELIVERED_TO_FAMILY || 0),
        caption: "left our custody",
        tone: "green" as const,
        icon: <CheckCircleOutlineOutlinedIcon />,
        // Multi-status bucket (RELEASED_TO_EXTERNAL_PARLOUR + DELIVERED_TO_FAMILY)
        // — inert for the same reason as "In Body Control".
        filterValue: undefined,
      },
    ],
    [statusCounts],
  );

  const todayIso = dayjs().format("YYYY-MM-DD");
  const todayTemperatureLogs = useMemo(
    () => temperatureLogs.filter((item) => item.readingDate === todayIso),
    [temperatureLogs, todayIso],
  );
  const todayHygieneChecklists = useMemo(
    () => hygieneChecklists.filter((item) => item.checklistDate === todayIso),
    [hygieneChecklists, todayIso],
  );
  const assignedStorageUnit = useMemo(
    () =>
      storageUnits.find(
        (unit) =>
          selectedRecord?.fridgeNumber &&
          selectedRecord?.shelfNumber &&
          String(unit.fridgeNumber || "").toLowerCase() === String(selectedRecord.fridgeNumber).toLowerCase() &&
          String(unit.shelfNumber || "").toLowerCase() === String(selectedRecord.shelfNumber).toLowerCase(),
      ),
    [storageUnits, selectedRecord?.fridgeNumber, selectedRecord?.shelfNumber],
  );
  const todayTemperatureByFridge = useMemo(() => {
    const fridgeNumbers = new Set<string>();
    storageUnits.forEach((unit) => {
      if (unit.fridgeNumber) fridgeNumbers.add(String(unit.fridgeNumber));
    });
    todayTemperatureLogs.forEach((log) => {
      if (log.fridgeNumber) fridgeNumbers.add(String(log.fridgeNumber));
    });

    return Array.from(fridgeNumbers)
      .sort((left, right) => left.localeCompare(right))
      .map((fridgeNumber) => ({
        fridgeNumber,
        am: todayTemperatureLogs.find((log) => log.fridgeNumber === fridgeNumber && log.shift === "AM"),
        pm: todayTemperatureLogs.find((log) => log.fridgeNumber === fridgeNumber && log.shift === "PM"),
      }));
  }, [storageUnits, todayTemperatureLogs]);
  const getTodayTemperature = (shift: "AM" | "PM") => todayTemperatureLogs.find((item) => item.shift === shift);
  const getTodayHygiene = (shift: "AM" | "PM") => todayHygieneChecklists.find((item) => item.shift === shift);
  const availableShelfCount = storageUnits.filter((item) => item.status === "AVAILABLE").length;
  const occupiedShelfCount = storageUnits.filter((item) => item.status === "OCCUPIED").length;
  const outOfServiceShelfCount = storageUnits.filter((item) => item.status === "OUT_OF_SERVICE").length;
  const availableStorageUnits = useMemo(
    () => storageUnits.filter((item) => item.status === "AVAILABLE"),
    [storageUnits],
  );
  const capacityProvinceOptions = useMemo(
    () =>
      Array.from(new Set(mortuaryCapacities.map((item) => item.province).filter(Boolean))).sort(),
    [mortuaryCapacities],
  );
  const capacityRegionOptions = useMemo(
    () =>
      Array.from(new Set(mortuaryCapacities.map((item) => item.region).filter(Boolean))).sort(),
    [mortuaryCapacities],
  );
  // The queue's "Mortuary" filter is a select, not free text — options come
  // from the mortuaries already loaded for the registers table above, not a
  // second, invented data source.
  const mortuaryNameOptions = useMemo(
    () =>
      Array.from(new Set(mortuaryCapacities.map((item) => item.mortuaryName).filter(Boolean))).sort(),
    [mortuaryCapacities],
  );
  const pagedMortuaryCapacities = useMemo(() => {
    // Clamp the same way StandardPager clamps its own page (StandardUI.tsx),
    // so a filter that shrinks the list can never leave the slice reading
    // from an offset past the end while the pager shows an earlier page.
    const capacityPageCount = Math.max(1, Math.ceil(mortuaryCapacities.length / QUEUE_PAGE_SIZE));
    const clampedCapacityPage = Math.min(Math.max(capacityPage, 0), capacityPageCount - 1);
    return mortuaryCapacities.slice(
      clampedCapacityPage * QUEUE_PAGE_SIZE,
      clampedCapacityPage * QUEUE_PAGE_SIZE + QUEUE_PAGE_SIZE,
    );
  }, [mortuaryCapacities, capacityPage]);
  const manageMenuItem = useMemo(
    () => mortuaryCapacities.find((item) => item.mortuaryName === manageMenuAnchor?.mortuaryName),
    [mortuaryCapacities, manageMenuAnchor],
  );
  const manageMenuHasSetup = Number(manageMenuItem?.maxBodyCapacity || 0) > 0;
  const capacityTotals = useMemo(
    () =>
      mortuaryCapacities.reduce(
        (total, item) => ({
          max: total.max + Number(item.maxBodyCapacity || 0),
          current: total.current + Number(item.currentBodiesAccommodated || 0),
          available: total.available + Number(item.availableCapacity || 0),
          setupNeeded: total.setupNeeded + (Number(item.maxBodyCapacity || 0) > 0 ? 0 : 1),
        }),
        { max: 0, current: 0, available: 0, setupNeeded: 0 },
      ),
    [mortuaryCapacities],
  );
  const storageMortuaryOptions = useMemo(() => {
    const names = new Set<string>();
    mortuaryCapacities.forEach((item) => {
      if (item.mortuaryName) names.add(item.mortuaryName);
    });
    if (formState.mortuaryName) names.add(formState.mortuaryName);
    return Array.from(names).sort();
  }, [mortuaryCapacities, formState.mortuaryName]);
  const selectedCapacitySummary = useMemo(
    () =>
      mortuaryCapacities.find(
        (item) =>
          selectedRecord?.mortuaryName &&
          normalizeName(item.mortuaryName) === normalizeName(selectedRecord.mortuaryName),
      ),
    [mortuaryCapacities, selectedRecord?.mortuaryName],
  );
  const temperatureAm = getTodayTemperature("AM");
  const temperaturePm = getTodayTemperature("PM");
  const hygieneAm = getTodayHygiene("AM");
  const hygienePm = getTodayHygiene("PM");

  const isStageReadOnly = dialogMode === "STAGE_VIEW" && stageDialogReadOnly;
  const storageSetupIncomplete =
    dialogMode === "STORAGE_UNIT" &&
    (!formState.mortuaryName || !formState.fridgeNumber || !formState.shelfNumber);
  const temperatureLogIncomplete =
    dialogMode === "TEMPERATURE_LOG" &&
    (!formState.mortuaryName || !formState.fridgeNumber || formState.temperatureCelsius === "");
  const hygieneChecklistIncomplete =
    dialogMode === "HYGIENE_CHECKLIST" &&
    (!formState.mortuaryName || !formState.completedBy || !formState.supervisorName);
  const disableDialogSubmit = saving || storageSetupIncomplete || temperatureLogIncomplete || hygieneChecklistIncomplete;

  // ─── Queue view ──────────────────────────────────────────────────────────────

  if (!canViewMortuary) {
    return (
      <OperationsPageShell
        eyebrow="Mortuary"
        title="Mortuary Operations"
        description="This workspace is restricted by your IFAS role."
      >
        <OperationsEmptyState
          title="No mortuary access"
          description="Ask an IFAS administrator or mortuary supervisor if you should work in this module."
        />
      </OperationsPageShell>
    );
  }

  if (!isDetailsView) {
    return (
      <OperationsPageShell
        eyebrow="Mortuary Management"
        title="Mortuary Queue"
        description="View all mortuary intake records first, then open a record to manage intake, body control stages, measurements, and downstream dispatches."
        actions={
          <Button
            variant="text"
            startIcon={<AutorenewOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={() => loadRecords()}
            sx={standardListGhostBtnSx}
          >
            Refresh
          </Button>
        }
      >
        <Box display="grid" gap={2}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 2, mb: 2.5 }}>
            {selectedMeta.map((item) => {
              const filterValue = item.filterValue;
              return (
                <Box key={item.label} sx={{ minWidth: 0 }}>
                  <StandardMetricCard
                    label={item.label}
                    value={item.value}
                    caption={item.caption}
                    tone={item.tone}
                    icon={item.icon}
                    compact
                    {...(filterValue
                      ? {
                          onClick: () => {
                            setSelectedStatus((prev) => (prev === filterValue ? "" : filterValue));
                            setPage(0);
                          },
                          selected: selectedStatus === filterValue,
                        }
                      : {})}
                  />
                </Box>
              );
            })}
          </Box>

          <Paper
            elevation={0}
            sx={{ display: "flex", flexWrap: "wrap", gap: 1, bgcolor: ifasColors.surface, border: `1px solid ${ifasColors.border}`, borderRadius: "12px", p: "0.6rem" }}
          >
            <TextField
              size="small"
              placeholder="Body no, removal no, case, register"
              aria-label="Search mortuary queue"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: ifasColors.muted }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: "1 1 280px", minWidth: 280, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: ifasColors.surface } }}
            />
            <TextField
              size="small"
              select
              aria-label="Filter by mortuary"
              value={mortuaryFilter}
              onChange={(e) => { setMortuaryFilter(e.target.value); setPage(0); }}
              SelectProps={{ displayEmpty: true }}
              sx={{ minWidth: 190, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: ifasColors.surface } }}
            >
              <MenuItem value="">All mortuaries</MenuItem>
              {mortuaryNameOptions.map((name) => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              aria-label="Filter by status"
              value={selectedStatus}
              onChange={(e) => { setSelectedStatus(e.target.value); setPage(0); }}
              SelectProps={{ displayEmpty: true }}
              sx={{ minWidth: 190, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: ifasColors.surface } }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {Object.keys(emptyCounts).map((status) => (
                <MenuItem key={status} value={status}>{formatOperationsStatus(status)}</MenuItem>
              ))}
            </TextField>
          </Paper>

          <Paper elevation={0} sx={standardListCardSx}>
            {loading ? <Loader isLoading={loading} /> : null}
            <StandardListCardHead title="Body records" count={queueTotal} />

            {records.length === 0 && !loading ? (
              <Box p={2}>
                <OperationsEmptyState
                  title="No mortuary records found"
                  description={
                    searchText || mortuaryFilter || selectedStatus
                      ? "No record matches these filters. Clear the search or choose a different status."
                      : "Body removals delivered into mortuary custody will appear here."
                  }
                />
              </Box>
            ) : (
              <>
                <Box sx={{ display: { xs: "grid", md: "none" }, gap: 1.25, p: 1.5 }}>
                  {records.map((item: any) => {
                    const resolvedStatus = normalizeMortuaryStatus(item.mortuaryStatus, item.intakeRegisteredAt);
                    const resolvedStage = normalizeCurrentStage(item.currentBodyControlStage, resolvedStatus, item.intakeRegisteredAt);
                    const stageIndex = STAGES.findIndex((stage) => stage.key === resolvedStage);
                    const openRecord = () => onSelectMortuaryRecord?.(item.id);
                    return (
                      <Paper
                        key={item.id}
                        variant="outlined"
                        role="button"
                        tabIndex={0}
                        aria-label={`Open mortuary record ${item.bodyNumber || item.bodyRemovalNumber || ""}`}
                        onClick={openRecord}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openRecord();
                          }
                        }}
                        sx={{
                          p: 1.5,
                          borderRadius: 2.5,
                          borderColor: ifasColors.border,
                          cursor: "pointer",
                          transition: "border-color .2s ease, box-shadow .2s ease, transform .2s ease",
                          "&:hover": { borderColor: ifasColors.orange, boxShadow: "0 8px 24px rgba(15,23,42,0.08)", transform: "translateY(-1px)" },
                          "&:focus-visible": { outline: `3px solid ${ifasColors.softOrange}`, borderColor: ifasColors.orange },
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1.25}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography component="span" sx={{ ...standardListCellPrimarySx, overflowWrap: "anywhere" }}>
                              {item.bodyNumber || "Pending body number"}
                            </Typography>
                            <Typography component="span" sx={standardListCellSecondarySx}>{item.bodyRemovalNumber}</Typography>
                          </Box>
                          <Box sx={{ flexShrink: 0 }}>
                            <StatusDotChip status={resolvedStatus} tone={mortuaryStatusTone(resolvedStatus)} compact />
                          </Box>
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography component="span" sx={{ ...standardListCellSecondarySx, mt: 0, textTransform: "uppercase", fontSize: "10.5px" }}>Case</Typography>
                            <Typography component="span" sx={{ ...standardListCellPrimarySx, fontSize: "12.5px", overflowWrap: "anywhere" }}>{item.caseNumber || "System Case"}</Typography>
                            <Typography component="span" sx={standardListCellSecondarySx}>{item.funeralFileNumber || "Not linked"}</Typography>
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography component="span" sx={{ ...standardListCellSecondarySx, mt: 0, textTransform: "uppercase", fontSize: "10.5px" }}>Location</Typography>
                            <Typography component="span" sx={{ ...standardListCellPrimarySx, fontSize: "12.5px", overflowWrap: "anywhere" }}>{item.mortuaryName || "Not captured"}</Typography>
                            <Typography component="span" sx={standardListCellSecondarySx}>Shelf {item.shelfNumber || "Not captured"}</Typography>
                          </Box>
                          <Box sx={{ minWidth: 0, gridColumn: "1 / -1" }}>
                            <Typography component="span" sx={{ ...standardListCellSecondarySx, mt: 0, textTransform: "uppercase", fontSize: "10.5px" }}>Stage</Typography>
                            <Typography component="span" sx={{ ...standardListCellPrimarySx, fontSize: "12.5px" }}>{formatOperationsStatus(resolvedStage)}</Typography>
                            <Typography component="span" sx={standardListCellSecondarySx}>{Math.max(stageIndex + 1, 0)} of {STAGES.length} stages</Typography>
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} mt={1.25} color={ifasColors.orange}>
                          <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                          <Typography fontSize={11.5} fontWeight={800}>Open record</Typography>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>

                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  <Box sx={standardListScrollSx}>
                    <Table sx={{ minWidth: 900 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={standardListHeadCellSx}>Body / number</TableCell>
                          <TableCell sx={standardListHeadCellSx}>Case</TableCell>
                          <TableCell sx={standardListHeadCellSx}>Location</TableCell>
                          <TableCell sx={standardListHeadCellSx}>Status</TableCell>
                          <TableCell sx={standardListHeadCellSx}>Stage</TableCell>
                          <TableCell sx={standardListHeadCellSx} align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {records.map((item: any) => {
                          const resolvedStatus = normalizeMortuaryStatus(item.mortuaryStatus, item.intakeRegisteredAt);
                          const resolvedStage = normalizeCurrentStage(item.currentBodyControlStage, resolvedStatus, item.intakeRegisteredAt);
                          const stageIndex = STAGES.findIndex((stage) => stage.key === resolvedStage);
                          return (
                            <TableRow
                              key={item.id}
                              tabIndex={0}
                              aria-label={`Open mortuary record ${item.bodyNumber || item.bodyRemovalNumber || ""}`}
                              sx={standardClickableRowSx}
                              onClick={() => onSelectMortuaryRecord?.(item.id)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  onSelectMortuaryRecord?.(item.id);
                                }
                              }}
                            >
                              <TableCell>
                                <Typography component="span" sx={standardListCellPrimarySx}>{item.bodyNumber || "Pending body number"}</Typography>
                                <Typography component="span" sx={standardListCellSecondarySx}>{item.bodyRemovalNumber}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography component="span" sx={standardListCellPrimarySx}>{item.caseNumber || "System Case"}</Typography>
                                <Typography component="span" sx={standardListCellSecondarySx}>{item.funeralFileNumber || "Not linked"}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography component="span" sx={standardListCellPrimarySx}>{item.mortuaryName || "Not captured"}</Typography>
                                <Typography component="span" sx={standardListCellSecondarySx}>Shelf {item.shelfNumber || "Not captured"}</Typography>
                              </TableCell>
                              <TableCell>
                                <StatusDotChip status={resolvedStatus} tone={mortuaryStatusTone(resolvedStatus)} compact />
                              </TableCell>
                              <TableCell>
                                <Typography component="span" sx={standardListCellPrimarySx}>{formatOperationsStatus(resolvedStage)}</Typography>
                                <Typography component="span" sx={standardListCellSecondarySx}>{Math.max(stageIndex + 1, 0)} of {STAGES.length} stages</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="text"
                                  sx={standardRowActionBtnSx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSelectMortuaryRecord?.(item.id);
                                  }}
                                >
                                  Open
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              </>
            )}
            <StandardPager page={page} total={queueTotal} pageSize={QUEUE_PAGE_SIZE} onPage={setPage} />
          </Paper>

          <Box display="flex" justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} gap={1.5} flexWrap="wrap">
            <Box sx={{ minWidth: 0, flex: "1 1 420px" }}>
              <Typography fontWeight={900} color={ifasColors.text} fontSize={14}>Mortuary Readiness &amp; Capacity</Typography>
              <Typography fontSize={12.25} color={ifasColors.muted} mt={0.25}>
                Maintain branch-level shelves, fridge temperature logs, and hygiene checks before allocating bodies.
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap" sx={{ flex: "0 1 auto" }}>
              <Chip size="small" label={`${capacityTotals.available} spaces available`} sx={{ height: 28, fontWeight: 900, bgcolor: STANDARD_TONES.green.bg, color: STANDARD_TONES.green.color }} />
              <Chip size="small" label={`${capacityTotals.current}/${capacityTotals.max} occupied`} sx={{ height: 28, fontWeight: 900, bgcolor: STANDARD_TONES.blue.bg, color: STANDARD_TONES.blue.color }} />
              {capacityTotals.setupNeeded ? (
                <Chip size="small" label={`${capacityTotals.setupNeeded} need setup`} sx={{ height: 28, fontWeight: 900, bgcolor: STANDARD_TONES.amber.bg, color: STANDARD_TONES.amber.color }} />
              ) : null}
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{ display: "flex", flexWrap: "wrap", gap: 1, bgcolor: ifasColors.surface, border: `1px solid ${ifasColors.border}`, borderRadius: "12px", p: "0.6rem" }}
          >
            <TextField
              size="small"
              select
              aria-label="Filter by province"
              value={capacityProvinceFilter}
              onChange={(e) => { setCapacityProvinceFilter(e.target.value); setCapacityPage(0); }}
              SelectProps={{ displayEmpty: true }}
              sx={{ minWidth: 190, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: ifasColors.surface } }}
            >
              <MenuItem value="">All provinces</MenuItem>
              {capacityProvinceOptions.map((province) => (
                <MenuItem key={province} value={province}>{province}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              aria-label="Filter by region"
              value={capacityRegionFilter}
              onChange={(e) => { setCapacityRegionFilter(e.target.value); setCapacityPage(0); }}
              SelectProps={{ displayEmpty: true }}
              sx={{ minWidth: 190, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: ifasColors.surface } }}
            >
              <MenuItem value="">All regions</MenuItem>
              {capacityRegionOptions.map((region) => (
                <MenuItem key={region} value={region}>{region}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              placeholder="Search mortuary name"
              aria-label="Search mortuary name"
              value={capacitySearchText}
              onChange={(e) => { setCapacitySearchText(e.target.value); setCapacityPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: ifasColors.muted }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: "1 1 280px", minWidth: 280, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: ifasColors.surface } }}
            />
          </Paper>

          <Paper elevation={0} sx={standardListCardSx}>
            {capacityLoading ? <Loader isLoading={capacityLoading} /> : null}
            <StandardListCardHead title="Mortuary registers" count={mortuaryCapacities.length} />

            {pagedMortuaryCapacities.length === 0 && !capacityLoading ? (
              <Box p={2}>
                <OperationsEmptyState title="No mortuaries found" description="Adjust province, region, or mortuary name filters." />
              </Box>
            ) : (
              <>
                <Box sx={{ display: { xs: "grid", md: "none" }, gap: 1.25, p: 1.5 }}>
                  {pagedMortuaryCapacities.map((item) => {
                    const hasSetup = Number(item.maxBodyCapacity || 0) > 0;
                    const full = Boolean(item.full);
                    const registerStatusLabel = !hasSetup ? "Setup needed" : full ? "Full" : "Available";
                    const maxCapacity = Number(item.maxBodyCapacity || 0);
                    const currentCount = Number(item.currentBodiesAccommodated || 0);
                    const percentFull = maxCapacity > 0 ? Math.round((currentCount / maxCapacity) * 100) : 0;
                    const openManageMenu = (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) =>
                      setManageMenuAnchor({ el: e.currentTarget as HTMLElement, mortuaryName: item.mortuaryName });
                    return (
                      <Paper
                        key={item.mortuaryName}
                        variant="outlined"
                        {...(canApproveMortuary
                          ? {
                              role: "button" as const,
                              tabIndex: 0,
                              "aria-label": `Manage ${item.mortuaryName}`,
                              onClick: openManageMenu,
                              onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  openManageMenu(e);
                                }
                              },
                            }
                          : {})}
                        sx={{
                          p: 1.5,
                          borderRadius: 2.5,
                          borderColor: ifasColors.border,
                          ...(canApproveMortuary
                            ? {
                                cursor: "pointer",
                                transition: "border-color .2s ease, box-shadow .2s ease, transform .2s ease",
                                "&:hover": { borderColor: ifasColors.orange, boxShadow: "0 8px 24px rgba(15,23,42,0.08)", transform: "translateY(-1px)" },
                                "&:focus-visible": { outline: `3px solid ${ifasColors.softOrange}`, borderColor: ifasColors.orange },
                              }
                            : null),
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1.25}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography component="span" sx={{ ...standardListCellPrimarySx, overflowWrap: "anywhere" }}>
                              {item.mortuaryName}
                            </Typography>
                            <Typography component="span" sx={standardListCellSecondarySx}>
                              {[item.region, item.province].filter(Boolean).join(", ") || "Location not captured"}
                            </Typography>
                          </Box>
                          <Box sx={{ flexShrink: 0 }}>
                            <StatusDotChip status={registerStatusLabel} tone={operationsStatusTone(registerStatusLabel)} compact />
                          </Box>
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1, mb: 1.25 }}>
                          <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: ifasColors.page, textAlign: "center", minWidth: 0 }}>
                            <Typography fontSize={10.5} color={ifasColors.muted} textTransform="uppercase" fontWeight={800}>Max</Typography>
                            <Typography fontSize={15} fontWeight={900} color={ifasColors.text} sx={{ fontVariantNumeric: "tabular-nums" }}>{maxCapacity}</Typography>
                          </Box>
                          <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: ifasColors.page, textAlign: "center", minWidth: 0 }}>
                            <Typography fontSize={10.5} color={ifasColors.muted} textTransform="uppercase" fontWeight={800}>Current</Typography>
                            <Typography fontSize={15} fontWeight={900} color={ifasColors.text} sx={{ fontVariantNumeric: "tabular-nums" }}>{currentCount}</Typography>
                          </Box>
                          <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: ifasColors.page, textAlign: "center", minWidth: 0 }}>
                            <Typography fontSize={10.5} color={ifasColors.muted} textTransform="uppercase" fontWeight={800}>Available</Typography>
                            <Typography fontSize={15} fontWeight={900} color={full ? STANDARD_TONES.red.color : STANDARD_TONES.green.color} sx={{ fontVariantNumeric: "tabular-nums" }}>{item.availableCapacity || 0}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ minWidth: 0, mb: canApproveMortuary ? 1.25 : 0 }}>
                          <Box sx={{ width: "100%", height: 6, borderRadius: "999px", bgcolor: STANDARD_TONES.grey.bg, overflow: "hidden" }}>
                            <Box sx={{ width: `${percentFull}%`, height: "100%", bgcolor: ifasColors.info }} />
                          </Box>
                          <Typography component="span" sx={{ ...standardListCellSecondarySx, mt: "4px" }}>{percentFull}% full</Typography>
                        </Box>
                        {canApproveMortuary ? (
                          <Box display="flex" justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="text"
                              sx={standardRowActionBtnSx}
                              onClick={(e) => {
                                e.stopPropagation();
                                setManageMenuAnchor({ el: e.currentTarget, mortuaryName: item.mortuaryName });
                              }}
                            >
                              Manage
                            </Button>
                          </Box>
                        ) : null}
                      </Paper>
                    );
                  })}
                </Box>

                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  <Box sx={standardListScrollSx}>
                    <Table size="small" sx={{ minWidth: 1020 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={standardListHeadCellSx}>Register</TableCell>
                          <TableCell sx={standardListHeadCellSx}>Status</TableCell>
                          <TableCell sx={standardListHeadCellSx} align="right">Max</TableCell>
                          <TableCell sx={standardListHeadCellSx} align="right">Current</TableCell>
                          <TableCell sx={standardListHeadCellSx} align="right">Available</TableCell>
                          <TableCell sx={standardListHeadCellSx}>Capacity</TableCell>
                          <TableCell sx={{ ...standardListHeadCellSx, minWidth: 238 }} align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedMortuaryCapacities.map((item) => {
                          const hasSetup = Number(item.maxBodyCapacity || 0) > 0;
                          const full = Boolean(item.full);
                          const registerStatusLabel = !hasSetup ? "Setup needed" : full ? "Full" : "Available";
                          const maxCapacity = Number(item.maxBodyCapacity || 0);
                          const currentCount = Number(item.currentBodiesAccommodated || 0);
                          const percentFull = maxCapacity > 0 ? Math.round((currentCount / maxCapacity) * 100) : 0;
                          return (
                            <TableRow
                              key={item.mortuaryName}
                              sx={standardClickableRowSx}
                              tabIndex={canApproveMortuary ? 0 : undefined}
                              aria-label={`Manage ${item.mortuaryName}`}
                              onClick={
                                canApproveMortuary
                                  ? (e) => setManageMenuAnchor({ el: e.currentTarget, mortuaryName: item.mortuaryName })
                                  : undefined
                              }
                              onKeyDown={
                                canApproveMortuary
                                  ? (e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setManageMenuAnchor({ el: e.currentTarget, mortuaryName: item.mortuaryName });
                                      }
                                    }
                                  : undefined
                              }
                            >
                              <TableCell>
                                <Typography component="span" sx={standardListCellPrimarySx}>{item.mortuaryName}</Typography>
                                <Typography component="span" sx={standardListCellSecondarySx}>
                                  {[item.region, item.province].filter(Boolean).join(", ") || "Location not captured"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <StatusDotChip status={registerStatusLabel} tone={operationsStatusTone(registerStatusLabel)} compact />
                              </TableCell>
                              <TableCell align="right">
                                <Typography component="span" sx={{ ...standardListCellPrimarySx, fontVariantNumeric: "tabular-nums" }}>{maxCapacity}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography component="span" sx={{ ...standardListCellPrimarySx, fontVariantNumeric: "tabular-nums" }}>{currentCount}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography component="span" sx={{ ...standardListCellPrimarySx, fontVariantNumeric: "tabular-nums" }}>{item.availableCapacity || 0}</Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ width: 78, height: 6, borderRadius: "999px", bgcolor: STANDARD_TONES.grey.bg, overflow: "hidden" }}>
                                  <Box sx={{ width: `${percentFull}%`, height: "100%", bgcolor: ifasColors.info }} />
                                </Box>
                                <Typography component="span" sx={{ ...standardListCellSecondarySx, mt: "4px" }}>{percentFull}% full</Typography>
                              </TableCell>
                              <TableCell align="right">
                                {canApproveMortuary ? (
                                  <Button
                                    size="small"
                                    variant="text"
                                    sx={standardRowActionBtnSx}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setManageMenuAnchor({ el: e.currentTarget, mortuaryName: item.mortuaryName });
                                    }}
                                  >
                                    Manage
                                  </Button>
                                ) : (
                                  <Typography component="span" sx={standardListCellSecondarySx}>View only</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              </>
            )}
            <StandardPager page={capacityPage} total={mortuaryCapacities.length} pageSize={QUEUE_PAGE_SIZE} onPage={setCapacityPage} />
          </Paper>
        </Box>

        <Menu
          anchorEl={manageMenuAnchor?.el}
          open={Boolean(manageMenuAnchor)}
          onClose={() => setManageMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              const name = manageMenuAnchor?.mortuaryName;
              setManageMenuAnchor(null);
              if (name) openStorageSetup(name);
            }}
          >
            {manageMenuHasSetup ? "Shelves" : "Setup Shelves"}
          </MenuItem>
          <MenuItem
            onClick={() => {
              const name = manageMenuAnchor?.mortuaryName;
              setManageMenuAnchor(null);
              if (name) openTemperatureLog(name);
            }}
          >
            Log Temperature
          </MenuItem>
          <MenuItem
            onClick={() => {
              const name = manageMenuAnchor?.mortuaryName;
              setManageMenuAnchor(null);
              if (name) openHygieneChecklist(name);
            }}
          >
            Hygiene Checklist
          </MenuItem>
        </Menu>

        <Dialog
          open={["STORAGE_UNIT", "TEMPERATURE_LOG", "HYGIENE_CHECKLIST"].includes(dialogMode || "")}
          onClose={resetForm}
          maxWidth="md"
          fullWidth
          BackdropProps={{
            sx: {
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              backgroundColor: "rgba(15, 23, 42, 0.54)",
            },
          }}
          PaperProps={{
            sx: {
              borderRadius: "18px",
              overflow: "hidden",
              boxShadow: "0 24px 70px rgba(15,23,42,0.24)",
            },
          }}
        >
          <DialogTitle sx={{ px: 3, py: 2.2, borderBottom: "1px solid #f1f5f9" }}>
            <Typography fontSize={20} fontWeight={900} color={ifasColors.text}>
              {getDialogTitle(dialogMode, stageKey)}
            </Typography>
            <Typography fontSize={12.5} color={ifasColors.muted} mt={0.35}>
              {formState.mortuaryName || "Select mortuary"}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 3, bgcolor: "#fbfdff" }}>
            {dialogMode === "STORAGE_UNIT" && (
              <Box>
                <SecLabel text="Shelf setup" />
                <Typography fontSize={12.25} color={ifasColors.muted} mb={1.25}>
                  Add one physical shelf for this mortuary. It becomes available for intake allocation.
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.5 }}>
                  <TextField size="small" select label="Mortuary" value={formState.mortuaryName || ""} onChange={(e) => updateField("mortuaryName", e.target.value)} sx={{ ...intakeSx, gridColumn: "1 / -1" }}>
                    <MenuItem value="">Select mortuary</MenuItem>
                    {storageMortuaryOptions.map((mortuaryName) => (
                      <MenuItem key={mortuaryName} value={mortuaryName}>{mortuaryName}</MenuItem>
                    ))}
                  </TextField>
                  <TextField size="small" label="Fridge number" placeholder="e.g. FRIDGE-1" value={formState.fridgeNumber || ""} onChange={(e) => updateField("fridgeNumber", e.target.value)} sx={intakeSx} />
                  <TextField size="small" label="Shelf number" placeholder="e.g. A1" value={formState.shelfNumber || ""} onChange={(e) => updateField("shelfNumber", e.target.value)} sx={intakeSx} />
                  <TextField size="small" select label="Status" value={formState.status || "AVAILABLE"} onChange={(e) => updateField("status", e.target.value)} sx={intakeSx}>
                    <MenuItem value="AVAILABLE">Available</MenuItem>
                    <MenuItem value="OUT_OF_SERVICE">Out of Service</MenuItem>
                  </TextField>
                  <TextField size="small" fullWidth multiline rows={3} label="Notes" value={formState.notes || ""} onChange={(e) => updateField("notes", e.target.value)} sx={{ ...intakeSx, gridColumn: "1 / -1" }} />
                </Box>
              </Box>
            )}

            {dialogMode === "TEMPERATURE_LOG" && (
              <Box>
                <SecLabel text="AM / PM temperature log" />
                <Typography fontSize={12.25} color={ifasColors.muted} mb={1.25}>
                  Capture the fridge temperature for the selected mortuary and shift.
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.5 }}>
                  <TextField size="small" select label="Mortuary" value={formState.mortuaryName || ""} onChange={(e) => updateField("mortuaryName", e.target.value)} sx={{ ...intakeSx, gridColumn: "1 / -1" }}>
                    <MenuItem value="">Select mortuary</MenuItem>
                    {storageMortuaryOptions.map((mortuaryName) => (
                      <MenuItem key={mortuaryName} value={mortuaryName}>{mortuaryName}</MenuItem>
                    ))}
                  </TextField>
                  <TextField size="small" label="Fridge number" value={formState.fridgeNumber || ""} onChange={(e) => updateField("fridgeNumber", e.target.value)} sx={intakeSx} />
                  <TextField size="small" label="Reading date" type="date" InputLabelProps={{ shrink: true }} value={formState.readingDate || ""} onChange={(e) => updateField("readingDate", e.target.value)} sx={intakeSx} />
                  <TextField size="small" select label="Shift" value={formState.shift || "AM"} onChange={(e) => updateField("shift", e.target.value)} sx={intakeSx}>
                    <MenuItem value="AM">AM</MenuItem>
                    <MenuItem value="PM">PM</MenuItem>
                  </TextField>
                  <TextField size="small" label="Temperature (C)" type="number" value={formState.temperatureCelsius || ""} onChange={(e) => updateField("temperatureCelsius", e.target.value)} sx={intakeSx} />
                  <TextField size="small" label="Logged by" value={formState.loggedBy || ""} onChange={(e) => updateField("loggedBy", e.target.value)} sx={intakeSx} />
                  <TextField size="small" label="Normal min (C)" type="number" value={formState.normalMinCelsius ?? -5} onChange={(e) => updateField("normalMinCelsius", e.target.value)} sx={intakeSx} />
                  <TextField size="small" label="Normal max (C)" type="number" value={formState.normalMaxCelsius ?? 5} onChange={(e) => updateField("normalMaxCelsius", e.target.value)} sx={intakeSx} />
                  <TextField size="small" fullWidth multiline rows={3} label="Corrective action for breach" value={formState.correctiveAction || ""} onChange={(e) => updateField("correctiveAction", e.target.value)} sx={{ ...intakeSx, gridColumn: "1 / -1" }} />
                </Box>
              </Box>
            )}

            {dialogMode === "HYGIENE_CHECKLIST" && (
              <Box>
                <SecLabel text="Hygiene and cleaning checklist" />
                <Typography fontSize={12.25} color={ifasColors.muted} mb={1.25}>
                  Complete the branch-level mortuary readiness checklist for the selected shift.
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.2 }}>
                  <TextField size="small" select label="Mortuary" value={formState.mortuaryName || ""} onChange={(e) => updateField("mortuaryName", e.target.value)} sx={{ ...intakeSx, gridColumn: "1 / -1" }}>
                    <MenuItem value="">Select mortuary</MenuItem>
                    {storageMortuaryOptions.map((mortuaryName) => (
                      <MenuItem key={mortuaryName} value={mortuaryName}>{mortuaryName}</MenuItem>
                    ))}
                  </TextField>
                  <TextField size="small" label="Checklist date" type="date" InputLabelProps={{ shrink: true }} value={formState.checklistDate || ""} onChange={(e) => updateField("checklistDate", e.target.value)} sx={intakeSx} />
                  <TextField size="small" select label="Shift" value={formState.shift || "AM"} onChange={(e) => updateField("shift", e.target.value)} sx={intakeSx}>
                    <MenuItem value="AM">AM</MenuItem>
                    <MenuItem value="PM">PM</MenuItem>
                  </TextField>
                  {[
                    ["protectiveClothingWorn", "Protective clothing worn"],
                    ["noEatingDrinkingSmoking", "No eating, drinking, or smoking"],
                    ["flyControlApplied", "Fly control applied"],
                    ["ventilationAndLightingOk", "Ventilation and lighting checked"],
                    ["wasteBinsClosedAndEmptied", "Waste bins closed and emptied"],
                    ["instrumentsCleanDryStored", "Instruments clean, dry, and stored"],
                    ["entrancesClosed", "Entrances closed"],
                    ["coldRoomAndTraysClean", "Cold room and trays clean"],
                    ["refrigerationDoorsSecure", "Refrigeration doors secure"],
                    ["floorsWallsSinksDisinfected", "Floors, walls, and sinks disinfected"],
                    ["coffinsCasketsDusted", "Coffins/caskets dusted"],
                    ["airFreshenersUsed", "Air fresheners used"],
                    ["laundryRosterFollowed", "Laundry roster followed"],
                    ["handsWashedWithDisinfectant", "Hands washed with disinfectant"],
                    ["showerCompletedEndOfDay", "Shower completed end of day"],
                    ["uvLightManaged", "UV light managed"],
                  ].map(([field, label]) => (
                    <FormControlLabel
                      key={field}
                      control={<Checkbox checked={Boolean(formState[field])} onChange={(e) => updateField(field, e.target.checked)} />}
                      label={label}
                    />
                  ))}
                  <TextField size="small" label="Completed by" value={formState.completedBy || ""} onChange={(e) => updateField("completedBy", e.target.value)} sx={intakeSx} />
                  <TextField size="small" label="Supervisor name" value={formState.supervisorName || ""} onChange={(e) => updateField("supervisorName", e.target.value)} sx={intakeSx} />
                  <TextField size="small" fullWidth multiline rows={3} label="Exceptions" value={formState.exceptions || ""} onChange={(e) => updateField("exceptions", e.target.value)} sx={{ ...intakeSx, gridColumn: "1 / -1" }} />
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.75, bgcolor: "#fff", borderTop: "1px solid #e5e7eb", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={resetForm}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                minWidth: 100,
                fontWeight: 800,
                color: ifasColors.text,
                borderColor: "#cbd5e1",
                bgcolor: "#fff",
                "&:hover": {
                  borderColor: ifasColors.text,
                  bgcolor: "#f8fafc",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={submitDialog}
              disabled={disableDialogSubmit}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                minWidth: 140,
                fontWeight: 900,
                bgcolor: "#ea580c",
                color: "#fff",
                "&:hover": { bgcolor: "#c2410c" },
                "&.Mui-disabled": {
                  bgcolor: "#e5e7eb",
                  color: "#64748b",
                  opacity: 1,
                },
              }}
            >
              {saving ? "Saving..." : getDialogSubmitLabel(dialogMode, stageKey)}
            </Button>
          </DialogActions>
        </Dialog>
      </OperationsPageShell>
    );
  }

  // ─── Detail view ─────────────────────────────────────────────────────────────

  const currentStatus = normalizeMortuaryStatus(selectedRecord?.mortuaryStatus, selectedRecord?.intakeRegisteredAt);

  if (showHistory && isDetailsView) {
    return (
      <MortuaryHistoryTimeline
        historyData={historyData}
        isLoading={historyLoading}
        recordSummary={selectedRecord}
        onBack={() => setShowHistory(false)}
      />
    );
  }

  return (
    <OperationsPageShell
      eyebrow="Mortuary Management"
      title="Mortuary Record Details"
      description="Open one mortuary record at a time to complete intake, move through body control, and start post-mortuary dispatch work."
      actions={
        <Box sx={operationsActionBarSx}>
          <Button
            variant="text"
            startIcon={<ArrowBackOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={() => onBack?.()}
            sx={standardListGhostBtnSx}
          >
            Back
          </Button>
          <Button
            variant="text"
            onClick={() => openHistory()}
            sx={{ ...standardOutlineBtnSx, minHeight: 35, fontSize: "12.5px", lineHeight: 1.4, px: 1.6 }}
          >
            History
          </Button>
        </Box>
      }
    >
      <Paper elevation={0} sx={operationsDetailPaperSx}>
        {detailLoading ? <Loader isLoading={detailLoading} /> : null}
        {detailLoading ? (
          <Box minHeight={160} />
        ) : !selectedRecord ? (
          <OperationsEmptyState title="No mortuary record selected" description="Choose a record from the mortuary queue to continue." />
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" gap={1.5} flexWrap="wrap">
              <Box>
                <Typography fontSize={24} fontWeight={900} color={ifasColors.text}>{selectedRecord.bodyRemovalNumber}</Typography>
                <Typography fontSize={13.5} color={ifasColors.muted} mt={0.35}>{selectedRecord.caseNumber || "System Case"}</Typography>
              </Box>
              <StatusDotChip status={currentStatus} tone={mortuaryStatusTone(currentStatus)} />
            </Box>

            <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
              {currentStatus === "PENDING_INTAKE" && canApproveMortuary && (
                <Button variant="contained" onClick={() => openDialog("INTAKE")} sx={{ textTransform: "none", borderRadius: 2 }}>
                  Register Intake
                </Button>
              )}
              {currentStatus !== "PENDING_INTAKE" && canApproveMortuary && (
                <Button variant="outlined" onClick={() => openDialog("MEASUREMENT")} sx={{ textTransform: "none", borderRadius: 2 }}>
                  Update Measurement
                </Button>
              )}
              {currentStatus !== "PENDING_INTAKE" && !["RELEASED_TO_EXTERNAL_PARLOUR", "DELIVERED_TO_FAMILY"].includes(currentStatus) && canApproveMortuary && (
                <>
                  <Button variant="outlined" onClick={() => openDialog("CREATE_INTERNAL_TRANSFER")} sx={{ textTransform: "none", borderRadius: 2 }}>Prepare Internal Transfer</Button>
                  <Button variant="outlined" onClick={() => openDialog("CREATE_EXTERNAL_RELEASE")} sx={{ textTransform: "none", borderRadius: 2 }}>Prepare External Release</Button>
                  <Button variant="outlined" onClick={() => openDialog("CREATE_FAMILY_DELIVERY")} sx={{ textTransform: "none", borderRadius: 2 }}>Prepare Family Delivery</Button>
                </>
              )}
            </Box>

            <Box mt={2}>
              <OperationsSectionHeader title="Storage & Branch Readiness" />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.15fr 1fr" }, gap: 1.25, mt: 1 }}>
                <Paper elevation={0} sx={{ border: `1px solid ${ifasColors.border}`, borderRadius: 2, p: 1.5, bgcolor: "#fff" }}>
                  <Typography fontWeight={900} color={ifasColors.text} fontSize={14}>This Body Storage</Typography>
                  <Box mt={1} display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1}>
                    <Box sx={{ border: "1px solid #eef2f7", borderRadius: 2, p: 1 }}>
                      <Typography fontSize={11.5} color={ifasColors.muted}>Assigned shelf</Typography>
                      <Typography fontWeight={900} fontSize={17} color={ifasColors.text}>
                        {selectedRecord.fridgeNumber && selectedRecord.shelfNumber ? `${selectedRecord.fridgeNumber} / ${selectedRecord.shelfNumber}` : "Not assigned"}
                      </Typography>
                      <Typography fontSize={11.5} color={assignedStorageUnit?.status === "OCCUPIED" ? "#16a34a" : "#d97706"}>
                        {assignedStorageUnit?.status ? formatOperationsStatus(assignedStorageUnit.status) : "Assign during intake"}
                      </Typography>
                    </Box>
                    <Box sx={{ border: "1px solid #eef2f7", borderRadius: 2, p: 1 }}>
                      <Typography fontSize={11.5} color={ifasColors.muted}>Branch capacity</Typography>
                      <Typography fontWeight={900} fontSize={17} color={ifasColors.text}>
                        {selectedCapacitySummary ? `${selectedCapacitySummary.availableCapacity}/${selectedCapacitySummary.maxBodyCapacity} available` : `${availableShelfCount} available`}
                      </Typography>
                      <Typography fontSize={11.5} color={ifasColors.muted}>
                        {selectedCapacitySummary
                          ? `${selectedCapacitySummary.currentBodiesAccommodated} occupied, ${selectedCapacitySummary.outOfServiceCapacity} out of service`
                          : `${occupiedShelfCount} occupied, ${outOfServiceShelfCount} out of service`}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
                <Paper elevation={0} sx={{ border: `1px solid ${ifasColors.border}`, borderRadius: 2, p: 1.5, bgcolor: "#fff" }}>
                  <Typography fontWeight={900} color={ifasColors.text} fontSize={14}>{selectedRecord.mortuaryName || "Mortuary"} Readiness Snapshot</Typography>
                  <Box mt={1} display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" gap={1}>
                    <Box sx={{ border: "1px solid #eef2f7", borderRadius: 2, p: 1 }}>
                      <Typography fontSize={11.5} color={ifasColors.muted}>Temperature</Typography>
                      <Typography fontWeight={900} color={temperatureAm && temperaturePm ? "#16a34a" : "#d97706"}>
                        {temperatureAm && temperaturePm ? "Complete" : "Due"}
                      </Typography>
                    </Box>
                    <Box sx={{ border: "1px solid #eef2f7", borderRadius: 2, p: 1 }}>
                      <Typography fontSize={11.5} color={ifasColors.muted}>Hygiene</Typography>
                      <Typography fontWeight={900} color={hygieneAm && hygienePm ? "#16a34a" : "#d97706"}>
                        {hygieneAm && hygienePm ? "Complete" : "Due"}
                      </Typography>
                    </Box>
                    <Box sx={{ border: "1px solid #eef2f7", borderRadius: 2, p: 1 }}>
                      <Typography fontSize={11.5} color={ifasColors.muted}>Shelves</Typography>
                      <Typography fontWeight={900} color={availableShelfCount ? "#16a34a" : "#d97706"}>
                        {availableShelfCount ? "Ready" : "Setup needed"}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography fontSize={12} color={ifasColors.muted} mt={1}>
                    Temperature, hygiene, and shelf master are maintained per mortuary from the queue screen.
                  </Typography>
                </Paper>
              </Box>
            </Box>

            <Box mt={2} sx={{ display: "none" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1.5} flexWrap="wrap">
                <Box>
                  <OperationsSectionHeader title={`Mortuary Readiness: ${selectedRecord.mortuaryName || "Not Captured"}`} />
                  <Typography fontSize={12.25} color={ifasColors.muted} mt={0.35}>
                    These are mortuary-wide controls for this branch/cold room, not only for this body record.
                  </Typography>
                </Box>
                {canApproveMortuary && (
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Button size="small" variant="outlined" onClick={() => openDialog("TEMPERATURE_LOG")} sx={{ textTransform: "none", borderRadius: 2 }}>Log Fridge Temperature</Button>
                    <Button size="small" variant="outlined" onClick={() => openDialog("HYGIENE_CHECKLIST")} sx={{ textTransform: "none", borderRadius: 2 }}>Daily Hygiene Checklist</Button>
                    <Button size="small" variant="outlined" onClick={() => openStorageSetup(selectedRecord?.mortuaryName || "")} sx={{ textTransform: "none", borderRadius: 2 }}>Shelf Master</Button>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" }, gap: 1.5, mt: 1.25 }}>
                <Paper elevation={0} sx={{ border: `1px solid ${ifasColors.border}`, borderRadius: 2, p: 1.5, bgcolor: "#fff" }}>
                  <Typography fontWeight={900} color={ifasColors.text} fontSize={14}>Fridge Temperature Checks</Typography>
                  <Typography color={ifasColors.muted} fontSize={12.25} mt={0.35}>AM/PM readings are tracked per fridge. Breach requires corrective action and alert.</Typography>
                  <Box display="grid" gap={0.85} mt={1.25}>
                    {todayTemperatureByFridge.length ? todayTemperatureByFridge.slice(0, 3).map((fridge) => (
                      <Box key={fridge.fridgeNumber} sx={{ border: "1px solid #eef2f7", borderRadius: 2, p: 1 }}>
                        <Typography fontSize={12.25} fontWeight={900} color={ifasColors.text}>{fridge.fridgeNumber}</Typography>
                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={0.75} mt={0.65}>
                          {(["AM", "PM"] as const).map((shift) => {
                            const log = shift === "AM" ? fridge.am : fridge.pm;
                            const statusLabel = !log ? "Due" : log.breach ? "Breach" : "Normal";
                            return (
                              <Box key={shift}>
                                <Typography fontSize={11} color={ifasColors.muted}>{shift}</Typography>
                                <Typography fontSize={13} fontWeight={900} color={!log ? "#d97706" : log.breach ? "#dc2626" : "#16a34a"}>
                                  {log ? `${log.temperatureCelsius}C · ${statusLabel}` : "Due"}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )) : (
                      <Box sx={{ border: "1px solid #eef2f7", borderRadius: 2, p: 1 }}>
                        <Typography fontSize={13} fontWeight={900} color="#d97706">No fridge readings today</Typography>
                        <Typography fontSize={11.5} color={ifasColors.muted}>Create shelves first or log temperature for the active fridge.</Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
                <Paper elevation={0} sx={{ border: `1px solid ${ifasColors.border}`, borderRadius: 2, p: 1.5, bgcolor: "#fff" }}>
                  <Typography fontWeight={900} color={ifasColors.text} fontSize={14}>Body Shelf Assignment</Typography>
                  <Typography color={ifasColors.muted} fontSize={12.25} mt={0.35}>Shelves are master data; one available shelf is assigned during intake and released on dispatch.</Typography>
                  <Box mt={1.25} sx={{ border: "1px solid #eef2f7", borderRadius: 2, p: 1 }}>
                    <Typography fontSize={11.5} color={ifasColors.muted}>Current assignment</Typography>
                    <Typography fontWeight={900} fontSize={17} color={ifasColors.text}>
                      {selectedRecord.fridgeNumber && selectedRecord.shelfNumber ? `${selectedRecord.fridgeNumber} / ${selectedRecord.shelfNumber}` : "Not assigned"}
                    </Typography>
                    <Typography fontSize={11.5} color={assignedStorageUnit?.status === "OCCUPIED" ? "#16a34a" : "#d97706"}>
                      {assignedStorageUnit?.status ? formatOperationsStatus(assignedStorageUnit.status) : "Assign during intake"}
                    </Typography>
                  </Box>
                  <Typography fontSize={12} color={ifasColors.muted} mt={1}>
                    {selectedCapacitySummary
                      ? `Max ${selectedCapacitySummary.maxBodyCapacity}, ${selectedCapacitySummary.currentBodiesAccommodated} occupied, ${selectedCapacitySummary.availableCapacity} available, ${selectedCapacitySummary.outOfServiceCapacity} out of service`
                      : `${availableShelfCount} available, ${occupiedShelfCount} occupied, ${outOfServiceShelfCount} out of service`}
                  </Typography>
                </Paper>
                <Paper elevation={0} sx={{ border: `1px solid ${ifasColors.border}`, borderRadius: 2, p: 1.5, bgcolor: "#fff" }}>
                  <Typography fontWeight={900} color={ifasColors.text} fontSize={14}>Daily Hygiene And Cleaning</Typography>
                  <Typography color={ifasColors.muted} fontSize={12.25} mt={0.35}>AM readiness and PM close-out are tracked for this mortuary.</Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} mt={1.25}>
                    {(["AM", "PM"] as const).map((shift) => {
                      const checklist = shift === "AM" ? hygieneAm : hygienePm;
                      return (
                        <Box key={shift} sx={{ border: "1px solid #eef2f7", borderRadius: 2, p: 1 }}>
                          <Typography fontSize={11.5} color={ifasColors.muted}>{shift} checklist</Typography>
                          <Typography fontWeight={900} fontSize={18} color={checklist ? "#16a34a" : "#d97706"}>
                            {checklist ? "Done" : "Due"}
                          </Typography>
                          <Typography fontSize={11.5} color={ifasColors.muted}>{checklist?.completedBy || "Not completed"}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "repeat(3, minmax(0, 1fr))" }, gap: 1.5, mt: 1.5 }}>
                <Paper elevation={0} sx={{ border: `1px solid ${ifasColors.border}`, borderRadius: 2, p: 1.5, bgcolor: "#fff" }}>
                  <Typography fontWeight={900} color={ifasColors.text} fontSize={13.5}>Temperature Logbook</Typography>
                  {temperatureLogs.slice(0, 5).map((item) => (
                    <Box key={item.id} display="grid" gridTemplateColumns="1fr auto" gap={1} py={0.75} borderBottom="1px solid #f1f5f9">
                      <Box>
                        <Typography fontSize={12.25} fontWeight={800}>{item.readingDate} {item.shift} · {item.fridgeNumber}</Typography>
                        {item.breach ? (
                          <Typography fontSize={11.25} color={item.correctiveAction ? "#16a34a" : "#dc2626"}>
                            {item.correctiveAction ? "Action captured" : "Corrective action pending"} · {item.alertSent ? "Alert sent" : "Alert pending"}
                          </Typography>
                        ) : (
                          <Typography fontSize={11.25} color={ifasColors.muted}>Within accepted range</Typography>
                        )}
                      </Box>
                      <Typography fontSize={12.25} fontWeight={900} color={item.breach ? "#dc2626" : "#16a34a"}>{item.temperatureCelsius}C</Typography>
                    </Box>
                  ))}
                  {!temperatureLogs.length && <Typography fontSize={12.25} color={ifasColors.muted} mt={1}>No temperature logs for this mortuary yet.</Typography>}
                </Paper>
                <Paper elevation={0} sx={{ border: `1px solid ${ifasColors.border}`, borderRadius: 2, p: 1.5, bgcolor: "#fff" }}>
                  <Typography fontWeight={900} color={ifasColors.text} fontSize={13.5}>Shelf Master List</Typography>
                  {storageUnits.slice(0, 6).map((item) => (
                    <Box key={item.id} display="grid" gridTemplateColumns="1fr auto" gap={1} py={0.75} borderBottom="1px solid #f1f5f9">
                      <Typography fontSize={12.25}>{item.fridgeNumber} / {item.shelfNumber}</Typography>
                      <StatusDotChip status={item.status} tone={mortuaryStorageTone(item.status)} />
                    </Box>
                  ))}
                  {!storageUnits.length && <Typography fontSize={12.25} color={ifasColors.muted} mt={1}>No shelves configured for this mortuary yet.</Typography>}
                </Paper>
                <Paper elevation={0} sx={{ border: `1px solid ${ifasColors.border}`, borderRadius: 2, p: 1.5, bgcolor: "#fff" }}>
                  <Typography fontWeight={900} color={ifasColors.text} fontSize={13.5}>Daily Hygiene Logbook</Typography>
                  {hygieneChecklists.slice(0, 5).map((item) => (
                    <Box key={item.id} display="grid" gridTemplateColumns="1fr auto" gap={1} py={0.75} borderBottom="1px solid #f1f5f9">
                      <Typography fontSize={12.25}>{item.checklistDate} {item.shift} · {item.completedBy}</Typography>
                      <StatusDotChip status={item.status} tone={mortuaryChecklistTone(item.status)} />
                    </Box>
                  ))}
                  {!hygieneChecklists.length && <Typography fontSize={12.25} color={ifasColors.muted} mt={1}>No hygiene checklist for this mortuary yet.</Typography>}
                </Paper>
              </Box>
            </Box>

            <Box mt={2}>
              <OperationsSectionHeader title="Record Overview" />
              <OperationsDetailGrid>
                <OperationsDetailField label="Body Removal Number" value={selectedRecord.bodyRemovalNumber} />
                <OperationsDetailField label="Body Number" value={selectedRecord.bodyNumber} />
                <OperationsDetailField label="Case Number" value={selectedRecord.caseNumber} />
                <OperationsDetailField label="Funeral File Number" value={selectedRecord.funeralFileNumber} />
                <OperationsDetailField label="Mortuary" value={selectedRecord.mortuaryName} />
                <OperationsDetailField label="Fridge Number" value={selectedRecord.fridgeNumber} />
                <OperationsDetailField label="Shelf Number" value={selectedRecord.shelfNumber} />
                <OperationsDetailField label="Deceased" value={selectedRecord.deceasedFullName} />
                <OperationsDetailField label="Deceased ID" value={selectedRecord.deceasedIdNumber} />
                <OperationsDetailField label="Family Representative" value={selectedRecord.familyRepresentativeName} />
                <OperationsDetailField label="Family Contact" value={selectedRecord.familyRepresentativeContact} />
                <OperationsDetailField label="Current Status" value={formatOperationsStatus(normalizeMortuaryStatus(selectedRecord.mortuaryStatus, selectedRecord.intakeRegisteredAt))} />
                <OperationsDetailField label="Current Stage" value={formatOperationsStatus(normalizeCurrentStage(selectedRecord.currentBodyControlStage, selectedRecord.mortuaryStatus, selectedRecord.intakeRegisteredAt))} />
                <OperationsDetailField label="Received At" value={formatDateTime(selectedRecord.receivedAt)} />
                <OperationsDetailField label="Intake Registered At" value={formatDateTime(selectedRecord.intakeRegisteredAt)} />
              </OperationsDetailGrid>
            </Box>

              {paymentStatus && paymentStatus.hasInvoice ? (
                <Box
                  mt={2}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: paymentStatus.blocked ? "#F2C1B6" : "#BFE3CE",
                    backgroundColor: paymentStatus.blocked ? "#FDF3F1" : "#F1F9F4",
                  }}
                >
                  <Typography fontWeight={800} fontSize={13}
                    color={paymentStatus.blocked ? "#B3341F" : "#1A7F4B"}>
                    {paymentStatus.blocked
                      ? `Payment not confirmed — body preparation on hold${paymentStatus.mode === "WARN" ? " (advisory)" : ""}`
                      : paymentStatus.aodApproved
                        ? `Released by Acknowledgement of Debt ${paymentStatus.aodNumber}`
                        : "Finance cleared — payment confirmed"}
                  </Typography>
                  <Typography fontSize={12} color="#4B5563" mt={0.5}>
                    {paymentStatus.reason} · {paymentStatus.invoiceCount} invoice(s) on this case
                  </Typography>
                </Box>
              ) : null}

            {financeBlockMessage ? (
              <Box
                mt={2}
                role="alert"
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: "1px solid #F2C1B6",
                  borderLeft: "4px solid #B3341F",
                  backgroundColor: "#FDF3F1",
                }}
              >
                <Typography fontWeight={800} fontSize={13} color="#B3341F">
                  Body preparation refused — finance clearance required
                </Typography>
                <Typography fontSize={12.5} color="#4B5563" mt={0.5}>
                  {financeBlockMessage}
                </Typography>
                <Typography fontSize={12} color="#6B7280" mt={0.75}>
                  Raise an Acknowledgement of Debt from the invoice in Revenue, or wait for finance
                  to confirm the payment. Both the GCOO and GCEO offices must approve an AoD before
                  preparation can start.
                </Typography>
              </Box>
            ) : null}

            <Box mt={2}>
              <OperationsSectionHeader title="Body Control Stages" />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 0.9 }}>
                {STAGES.map((stage) => {
                  const status = stageStatusValue(selectedRecord, stage.key);
                  const blockedReason = getStageStartBlockedReason(selectedRecord, stage.key);
                  const measurementMissing = !hasMortuaryMeasurementDetails(selectedRecord);
                  const washingMeasurementRequired = stage.key === "WASHING" && status === "IN_PROGRESS" && measurementMissing && canApproveMortuary;
                  const stageBlockedReason = blockedReason;
                  const canStart = status === "NOT_STARTED" && canOperateMortuaryStage;
                  const canComplete = status === "IN_PROGRESS" && canOperateMortuaryStage && !washingMeasurementRequired;
                  const canView = status === "COMPLETED";
                  const showAction = canStart || canComplete || canView || washingMeasurementRequired;
                  const signedOffBy = getStageSummaryValue(selectedRecord, stage.key, "supervisorName");
                  return (
                    <Paper
                      key={stage.key}
                      elevation={0}
                      sx={{
                        px: 1.75,
                        py: 1.35,
                        borderRadius: "14px",
                        border: `1px solid ${ifasColors.border}`,
                        bgcolor: "#fff",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        transition: "border-color 0.18s ease, box-shadow 0.18s ease",
                        "&:hover": {
                          borderColor: "#fed7aa",
                          boxShadow: "0 10px 24px rgba(15,23,42,0.04)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: showAction ? { xs: "1fr", sm: "minmax(0, 1fr) auto" } : "1fr",
                          gap: 0.9,
                          alignItems: "center",
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={900} color={ifasColors.text} fontSize={14.25} lineHeight={1.15}>
                            {stage.label}
                          </Typography>
                          <Box sx={{ mt: 0.45 }}>
                            <StatusDotChip status={status} tone={mortuaryStageTone(status)} />
                          </Box>
                          {canView && signedOffBy ? (
                            <Typography
                              fontSize={11.15}
                              color={ifasColors.muted}
                              mt={0.65}
                              maxWidth={380}
                              lineHeight={1.3}
                              textAlign="center"
                              width="100%"
                            >
                              Signed off by {signedOffBy}
                            </Typography>
                          ) : null}
                          {canStart && stageBlockedReason ? (
                            <Typography
                              fontSize={11.25}
                              color={ifasColors.muted}
                              mt={0.7}
                              maxWidth={380}
                              lineHeight={1.3}
                            >
                              {stageBlockedReason}
                            </Typography>
                          ) : null}
                          {washingMeasurementRequired ? (
                            <Typography
                              fontSize={11.25}
                              color={ifasColors.muted}
                              mt={0.7}
                              maxWidth={380}
                              lineHeight={1.3}
                            >
                              General Assistants must capture body measurements during washing before washing can be signed off.
                            </Typography>
                          ) : null}
                        </Box>
                        {showAction ? (
                          <Box
                            display="flex"
                            gap={1}
                            flexWrap="wrap"
                            justifyContent="flex-end"
                            alignItems="center"
                          >
                            {washingMeasurementRequired && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => openDialog("MEASUREMENT")}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: "12px",
                                  minWidth: 124,
                                  height: 36,
                                  px: 1.6,
                                  fontWeight: 800,
                                  fontSize: 13,
                                  bgcolor: "#f97316",
                                  "&:hover": {
                                    bgcolor: "#ea580c",
                                  },
                                }}
                              >
                                Add Measurement
                              </Button>
                            )}
                            {canStart && (
                              <Button
                                size="small"
                                variant="contained"
                                disabled={Boolean(stageBlockedReason)}
                                onClick={() => handleStartStage(stage.key)}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: "12px",
                                  minWidth: 88,
                                  height: 36,
                                  px: 1.5,
                                  fontWeight: 800,
                                  fontSize: 13,
                                  "&.Mui-disabled": {
                                    bgcolor: "#f5f7fb",
                                    color: "#9ca3af",
                                    boxShadow: "none",
                                  },
                                }}
                              >
                                {stageActionLoadingKey === stage.key ? "Starting..." : "Start"}
                              </Button>
                            )}
                            {canComplete && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => openDialog("STAGE_COMPLETE", stage.key)}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: "12px",
                                  minWidth: 98,
                                  height: 36,
                                  px: 1.75,
                                  fontWeight: 800,
                                  fontSize: 13,
                                }}
                              >
                                Complete
                              </Button>
                            )}
                            {canView && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => openDialog("STAGE_VIEW", stage.key)}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: "12px",
                                  minWidth: 86,
                                  height: 36,
                                  px: 1.4,
                                  fontWeight: 700,
                                  fontSize: 12.5,
                                  borderColor: "#fed7aa",
                                  color: "#c2410c",
                                  bgcolor: "#fff7ed",
                                  "&:hover": {
                                    borderColor: "#fdba74",
                                    bgcolor: "#ffedd5",
                                  },
                                }}
                              >
                                View
                              </Button>
                            )}
                          </Box>
                        ) : null}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Box>

            <Box mt={2}>
              <OperationsSectionHeader title="Washing Measurements And Coffining Readiness" />
              <OperationsDetailGrid>
                <OperationsDetailField label="Casket Type" value={selectedRecord.measurementCasketType} />
                <OperationsDetailField label="Length (cm)" value={selectedRecord.measurementLengthCm} />
                <OperationsDetailField label="Width (cm)" value={selectedRecord.measurementWidthCm} />
                <OperationsDetailField label="Height (cm)" value={selectedRecord.measurementHeightCm} />
                <OperationsDetailField label="Measured Size" value={selectedRecord.measuredSizeCategory} />
                <OperationsDetailField label="Line Manager Confirmed" value={formatBool(selectedRecord.coffinTypeConfirmedByLineManager)} />
                <OperationsDetailField label="Confirmed By" value={selectedRecord.coffinTypeConfirmedBy} />
                <OperationsDetailField label="Confirmed At" value={formatDateTime(selectedRecord.coffinTypeConfirmedAt)} />
              </OperationsDetailGrid>
            </Box>

            <Box mt={2} sx={{ display: "none" }}>
              <OperationsSectionHeader title="Mortuary Compliance & Environment" />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 2,
                  mt: 1.5,
                }}
              >
                {/* Temperature */}
                <Paper
                  elevation={0}
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "10px",
                    overflow: "hidden",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography
                      fontSize={14}
                      fontWeight={800}
                      color="#1F2937"
                    >
                      🌡 Temperature
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 4,
                        mt: 2,
                      }}
                    >
                      <Box>
                        <Typography fontSize={11} color="#9CA3AF">
                          Normal
                        </Typography>
                        <Typography
                          fontSize={22}
                          fontWeight={800}
                          color="#16A34A"
                        >
                          2
                        </Typography>
                      </Box>

                      <Box>
                        <Typography fontSize={11} color="#9CA3AF">
                          Breach
                        </Typography>
                        <Typography
                          fontSize={22}
                          fontWeight={800}
                          color="#DC2626"
                        >
                          1
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Button
                    fullWidth
                    size="small"
                    variant="text"
                    onClick={() => openDialog("TEMPERATURE_LOG")}
                    sx={{
                      borderTop: "1px solid #F1F5F9",
                      borderRadius: 0,
                      py: 1,
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "none",
                    }}
                  >
                    View Temperature Logs →
                  </Button>
                </Paper>

                {/* Fridge & Shelf */}
                <Paper
                  elevation={0}
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "10px",
                    overflow: "hidden",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography
                      fontSize={14}
                      fontWeight={800}
                      color="#1F2937"
                    >
                      🗄 Fridge & Shelf
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 4,
                        mt: 2,
                      }}
                    >
                      <Box>
                        <Typography fontSize={11} color="#9CA3AF">
                          Available
                        </Typography>
                        <Typography
                          fontSize={22}
                          fontWeight={800}
                          color="#16A34A"
                        >
                          1
                        </Typography>
                      </Box>

                      <Box>
                        <Typography fontSize={11} color="#9CA3AF">
                          Occupied
                        </Typography>
                        <Typography
                          fontSize={22}
                          fontWeight={800}
                          color="#374151"
                        >
                          2
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Button
                    fullWidth
                    size="small"
                    variant="text"
                    onClick={() => openDialog("STORAGE_UNIT")}
                    sx={{
                      borderTop: "1px solid #F1F5F9",
                      borderRadius: 0,
                      py: 1,
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "none",
                    }}
                  >
                    Manage Shelves →
                  </Button>
                </Paper>

                {/* Hygiene */}
                <Paper
                  elevation={0}
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "10px",
                    overflow: "hidden",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography
                      fontSize={14}
                      fontWeight={800}
                      color="#1F2937"
                    >
                      🧼 Hygiene
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 4,
                        mt: 2,
                      }}
                    >
                      <Box>
                        <Typography fontSize={11} color="#9CA3AF">
                          Completed
                        </Typography>
                        <Typography
                          fontSize={22}
                          fontWeight={800}
                          color="#16A34A"
                        >
                          1
                        </Typography>
                      </Box>

                      <Box>
                        <Typography fontSize={11} color="#9CA3AF">
                          Pending
                        </Typography>
                        <Typography
                          fontSize={22}
                          fontWeight={800}
                          color="#D97706"
                        >
                          1
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Button
                    fullWidth
                    size="small"
                    variant="text"
                    onClick={() => openDialog("HYGIENE_CHECKLIST")}
                    sx={{
                      borderTop: "1px solid #F1F5F9",
                      borderRadius: 0,
                      py: 1,
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "none",
                    }}
                  >
                    View Hygiene Checklist →
                  </Button>
                </Paper>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* ═══════════════════════════════════════════════════════════════════════
          SHARED DIALOG — all forms live inside DialogContent
      ═══════════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={Boolean(dialogMode)}
        onClose={resetForm}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        BackdropProps={{
          sx: {
            backdropFilter: "blur(6px)",
            backgroundColor: "rgba(15,23,42,0.4)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 25px 80px rgba(0,0,0,0.22)",
            maxHeight: "92vh",
          },
        }}
      >
        {/* ── Header ── */}
        <DialogTitle
          sx={{
            px: 3,
            py: 2.35,
            borderBottom: "1px solid #f1f5f9",
            bgcolor: "#fff",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                bgcolor: "#fff7ed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#f97316",
                border: "1px solid #fed7aa",
                flexShrink: 0,
                boxShadow: "0 10px 20px rgba(249,115,22,0.08)",
              }}
            >
              {getDialogIcon(dialogMode)}
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#111827",
                  lineHeight: 1.2,
                }}
              >
                {getDialogTitle(dialogMode, stageKey)}
              </Typography>

              <Typography
                sx={{
                  fontSize: 12,
                  color: "#94a3b8",
                  mt: 0.4,
                }}
              >
                {getDialogSubtitle(selectedRecord)}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        {/* ── Body ── */}
        <DialogContent
          dividers={false}
          sx={{
            p: 0,
            bgcolor: "#f8fafc",
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(156,163,175,0.35) transparent",
            "&::-webkit-scrollbar": { width: "5px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: "rgba(156,163,175,0.3)", borderRadius: "999px" },
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: 2.75,
              pb: 2.25,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              "& > .MuiBox-root": {
                border: "1px solid #e8edf3",
                borderRadius: "20px",
                background: "linear-gradient(180deg, #ffffff 0%, #fcfcfd 100%)",
                p: 2.25,
                boxShadow: "0 14px 32px rgba(15,23,42,0.05)",
              },
            }}
          >

            {/* ══════════════════════════════════════════════════
                INTAKE FORM
            ══════════════════════════════════════════════════ */}
            {/* ══════════════════════════════════════════════════
                INTAKE FORM
            ══════════════════════════════════════════════════ */}
            {dialogMode === "INTAKE" && (
              <>
                {/* Registration */}
                <Box>
                  <SecLabel text="Registration" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <TextField size="small" label="Registered by" value={formState.intakeRegisteredBy || ""} onChange={(e) => updateField("intakeRegisteredBy", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Destination mortuary" value={selectedRecord?.mortuaryName || "Not captured"} InputProps={{ readOnly: true }} sx={{ ...intakeSx, "& .MuiInputBase-root": { bgcolor: "#f9fafb", "& input": { color: "#9ca3af" } } }} />
                  </Box>
                </Box>

                {/* Identification Tags */}
                <Box>
                  <SecLabel text="Identification tags" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.25 }}>
                    {[
                      { label: "Toe tag", nameField: "toeTagName", numField: "toeTagNumber", placeholder: "TT-0041" },
                      { label: "Wrist tag", nameField: "wristTagName", numField: "wristTagNumber", placeholder: "WT-0041" },
                      { label: "Belongings bag", nameField: "belongingsBagTagName", numField: "belongingsBagTagNumber", placeholder: "BB-0041" },
                    ].map((tag) => (
                      <Box key={tag.label} sx={{ border: "1px solid #fed7aa", borderRadius: 2, overflow: "hidden" }}>
                        <Box sx={{ bgcolor: "#fff7ed", px: 1.5, py: 0.85, borderBottom: "1px solid #fed7aa" }}>
                          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "#92400e" }}>{tag.label}</Typography>
                        </Box>
                        <Box sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
                          <Box>
                            <Typography sx={{ fontSize: 10.5, color: "#9ca3af", mb: 0.5 }}>Name</Typography>
                            <TextField size="small" fullWidth value={formState[tag.nameField] || ""} onChange={(e) => updateField(tag.nameField, e.target.value)} sx={tagInnerSx} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: 10.5, color: "#9ca3af", mb: 0.5 }}>Number</Typography>
                            <TextField size="small" fullWidth placeholder={tag.placeholder} value={formState[tag.numField] || ""} onChange={(e) => updateField(tag.numField, e.target.value)} sx={tagInnerSx} />
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Body Condition */}
                <Box>
                  <SecLabel text="Body condition" />
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 1, mb: 1.5 }}>
                    {[
                      { value: "Good", icon: "✅", bg: "#f0fdf4", border: "#86efac", text: "#166534" },
                      { value: "Fair", icon: "⚠️", bg: "#fffbeb", border: "#fcd34d", text: "#92400e" },
                      { value: "Poor", icon: "❌", bg: "#fef2f2", border: "#fca5a5", text: "#991b1b" },
                    ].map((opt) => {
                      const selected = formState._conditionOption === opt.value;
                      return (
                        <Box key={opt.value} onClick={() => updateField("_conditionOption", opt.value)}
                          sx={{ border: `1px solid ${selected ? opt.border : "#e5e7eb"}`, borderRadius: 2, py: 1.25, px: 1, textAlign: "center", cursor: "pointer", bgcolor: selected ? opt.bg : "transparent", transition: "all 0.15s", userSelect: "none", "&:hover": { borderColor: opt.border, bgcolor: opt.bg } }}>
                          <Typography sx={{ fontSize: 18, mb: 0.5 }}>{opt.icon}</Typography>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: selected ? opt.text : "#6b7280" }}>{opt.value}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    <TextField size="small" fullWidth label="Condition description" multiline rows={2} value={formState.bodyConditionAtArrival || ""} onChange={(e) => updateField("bodyConditionAtArrival", e.target.value)} placeholder="Describe visible condition, injuries, or notable observations at arrival…" sx={intakeSx} />
                    <TextField size="small" fullWidth label="Intake notes" multiline rows={2} value={formState.intakeNotes || ""} onChange={(e) => updateField("intakeNotes", e.target.value)} placeholder="Special instructions or additional notes…" sx={intakeSx} />
                  </Box>
                </Box>
              </>
            )}

            {/* ══════════════════════════════════════════════════
                MEASUREMENT FORM
            ══════════════════════════════════════════════════ */}
            {dialogMode === "MEASUREMENT" && (
              <>
                {/* Storage Unit Allocation */}
                <Box>
                  <SecLabel text="Mortuary Fridge & Shelf Allocation" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <TextField
                      size="small"
                      select
                      label="Available fridge / shelf no"
                      value={formState.fridgeNumber && formState.shelfNumber ? `${formState.fridgeNumber}|||${formState.shelfNumber}` : ""}
                      onChange={(e) => {
                        const [fridgeNumber, shelfNumber] = String(e.target.value).split("|||");
                        updateField("fridgeNumber", fridgeNumber);
                        updateField("shelfNumber", shelfNumber);
                      }}
                      helperText={
                        availableStorageUnits.length
                          ? `${availableStorageUnits.length} available shelves in ${selectedRecord?.mortuaryName || "this mortuary"}`
                          : "No available shelves configured for this mortuary"
                      }
                      sx={intakeSx}
                    >
                      <MenuItem value="">Select fridge / shelf no</MenuItem>
                      {availableStorageUnits.map((unit) => (
                        <MenuItem key={unit.id || storageOptionValue(unit)} value={storageOptionValue(unit)}>
                          {unit.fridgeNumber} / {unit.shelfNumber}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>

                <Box>
                  <SecLabel text="Casket & dimensions" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <TextField size="small" label="Casket Type" value={formState.casketType || ""} onChange={(e) => updateField("casketType", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Measurement Date" type="date" InputLabelProps={{ shrink: true }} value={formState.measurementDate || ""} onChange={(e) => updateField("measurementDate", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Length (cm)" type="number" value={formState.measurementLengthCm || ""} onChange={(e) => updateField("measurementLengthCm", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Width (cm)" type="number" value={formState.measurementWidthCm || ""} onChange={(e) => updateField("measurementWidthCm", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Height (cm)" type="number" value={formState.measurementHeightCm || ""} onChange={(e) => updateField("measurementHeightCm", e.target.value)} sx={intakeSx} />
                  </Box>
                </Box>
                <Box>
                  <SecLabel text="Measuring officer" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <TextField size="small" label="Employee Names" value={formState.measuringEmployeeNames || ""} onChange={(e) => updateField("measuringEmployeeNames", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Designation" value={formState.measuringDesignation || ""} onChange={(e) => updateField("measuringDesignation", e.target.value)} sx={intakeSx} />
                    <SignatureUploadField
                      label="Signature"
                      module="MORTUARY"
                      referenceNumber={selectedRecord?.bodyRemovalNumber || selectedRecord?.id}
                      fieldName="measuringSignature"
                      value={formState.measuringSignature || ""}
                      onUploaded={(fileKey) => updateField("measuringSignature", fileKey)}
                    />
                  </Box>
                </Box>
                <Box>
                  <SecLabel text="Line manager confirmation" />
                  <FormControlLabel control={<Checkbox checked={Boolean(formState.coffinTypeConfirmedByLineManager)} onChange={(e) => updateField("coffinTypeConfirmedByLineManager", e.target.checked)} />} label="Line manager confirmed coffin type" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, mt: 1 }}>
                    <TextField size="small" label="Confirmed By" value={formState.coffinTypeConfirmedBy || ""} onChange={(e) => updateField("coffinTypeConfirmedBy", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Confirmed At" type="datetime-local" InputLabelProps={{ shrink: true }} value={formState.coffinTypeConfirmedAt || ""} onChange={(e) => updateField("coffinTypeConfirmedAt", e.target.value)} sx={intakeSx} />
                  </Box>
                </Box>
              </>
            )}

            {/* ══════════════════════════════════════════════════
                STAGE START / COMPLETE FORM
            ══════════════════════════════════════════════════ */}
            {(dialogMode === "STAGE_COMPLETE" || dialogMode === "STAGE_VIEW") && (
              <>
                <Box>
                  <SecLabel text={isStageReadOnly ? "Saved supervisor sign-off" : "Supervisor sign-off"} />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <TextField
                      size="small"
                      label="Supervisor Name"
                      value={formState.supervisorName || ""}
                      onChange={(e) => updateField("supervisorName", e.target.value)}
                      InputProps={isStageReadOnly ? { readOnly: true } : undefined}
                      sx={intakeSx}
                    />
                    {isStageReadOnly ? (
                      <Box sx={{ display: "grid", gap: 0.85 }}>
                        <TextField
                          size="small"
                          label="Supervisor Signature"
                          value={deriveStoredFileName(formState.supervisorSignature)}
                          InputProps={{ readOnly: true }}
                          sx={intakeSx}
                        />
                        <Box display="flex" justifyContent="flex-start">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
                            onClick={() => previewSignatureFile(formState.supervisorSignature)}
                            disabled={!formState.supervisorSignature}
                            sx={{
                              textTransform: "none",
                              borderRadius: "10px",
                              fontWeight: 700,
                              borderColor: "#fed7aa",
                              color: "#c2410c",
                              bgcolor: "#fff7ed",
                              "&:hover": {
                                borderColor: "#fdba74",
                                bgcolor: "#ffedd5",
                              },
                            }}
                          >
                            View File
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <SignatureUploadField
                        label="Supervisor Signature"
                        module="MORTUARY"
                        referenceNumber={selectedRecord?.bodyRemovalNumber || selectedRecord?.id}
                        fieldName={`${stageKey || "stage"}SupervisorSignature`}
                        value={formState.supervisorSignature || ""}
                        onUploaded={(fileKey) => updateField("supervisorSignature", fileKey)}
                      />
                    )}
                  </Box>
                </Box>

                {stageKey === "WASHING" && (
                  <Box>
                    <SecLabel text="Washing details" />
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                      <TextField size="small" label="GA 1" value={formState.washingGa1 || ""} onChange={(e) => updateField("washingGa1", e.target.value)} InputProps={isStageReadOnly ? { readOnly: true } : undefined} sx={intakeSx} />
                      <TextField size="small" label="GA 2" value={formState.washingGa2 || ""} onChange={(e) => updateField("washingGa2", e.target.value)} InputProps={isStageReadOnly ? { readOnly: true } : undefined} sx={intakeSx} />
                    </Box>
                  </Box>
                )}

                {stageKey === "CLOTHES" && (
                  <Box>
                    <SecLabel text="Clothes details" />
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                      <TextField size="small" label="Clothes Received Date" type="date" InputLabelProps={{ shrink: true }} value={formState.clothesReceivedDate || ""} onChange={(e) => updateField("clothesReceivedDate", e.target.value)} InputProps={isStageReadOnly ? { readOnly: true } : undefined} sx={intakeSx} />
                      <TextField size="small" label="Top Description" value={formState.clothesTopDescription || ""} onChange={(e) => updateField("clothesTopDescription", e.target.value)} InputProps={isStageReadOnly ? { readOnly: true } : undefined} sx={intakeSx} />
                      <TextField size="small" label="Bottom Description" value={formState.clothesBottomDescription || ""} onChange={(e) => updateField("clothesBottomDescription", e.target.value)} InputProps={isStageReadOnly ? { readOnly: true } : undefined} sx={intakeSx} />
                      <TextField size="small" label="Underneath Description" value={formState.clothesUnderneathDescription || ""} onChange={(e) => updateField("clothesUnderneathDescription", e.target.value)} InputProps={isStageReadOnly ? { readOnly: true } : undefined} sx={intakeSx} />
                      <TextField size="small" label="Special Requests" value={formState.clothesSpecialRequests || ""} onChange={(e) => updateField("clothesSpecialRequests", e.target.value)} InputProps={isStageReadOnly ? { readOnly: true } : undefined} sx={intakeSx} />
                    </Box>
                    <Box sx={{ display: "grid", gap: 0.25, mt: 1 }}>
                      <FormControlLabel
                        disabled={isStageReadOnly}
                        control={<Checkbox checked={Boolean(formState.clothesInsideTagDone)} onChange={(e) => updateField("clothesInsideTagDone", e.target.checked)} />}
                        label="Inside bag tag completed"
                      />
                      <FormControlLabel
                        disabled={isStageReadOnly}
                        control={<Checkbox checked={Boolean(formState.clothesOutsideTagDone)} onChange={(e) => updateField("clothesOutsideTagDone", e.target.checked)} />}
                        label="Outside bag tag completed"
                      />
                    </Box>
                  </Box>
                )}

                {stageKey === "DRESSING" && (
                  <Box>
                    <SecLabel text="Dressing details" />
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                      <TextField size="small" label="GA 1" value={formState.dressingGa1 || ""} onChange={(e) => updateField("dressingGa1", e.target.value)} InputProps={isStageReadOnly ? { readOnly: true } : undefined} sx={intakeSx} />
                      <TextField size="small" label="GA 2" value={formState.dressingGa2 || ""} onChange={(e) => updateField("dressingGa2", e.target.value)} InputProps={isStageReadOnly ? { readOnly: true } : undefined} sx={intakeSx} />
                    </Box>
                    <FormControlLabel sx={{ mt: 1 }} disabled={isStageReadOnly} control={<Checkbox checked={Boolean(formState.dressingTagVerified)} onChange={(e) => updateField("dressingTagVerified", e.target.checked)} />} label="Toe and wrist tags verified against deceased details" />
                  </Box>
                )}

                {stageKey === "COFFINING" && (
                  <Box>
                    <SecLabel text="Coffining details" />
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                      <TextField size="small" label="GA 1" value={formState.coffiningGa1 || ""} onChange={(e) => updateField("coffiningGa1", e.target.value)} InputProps={isStageReadOnly ? { readOnly: true } : undefined} sx={intakeSx} />
                      <TextField size="small" label="GA 2" value={formState.coffiningGa2 || ""} onChange={(e) => updateField("coffiningGa2", e.target.value)} InputProps={isStageReadOnly ? { readOnly: true } : undefined} sx={intakeSx} />
                    </Box>
                    <FormControlLabel sx={{ mt: 1 }} disabled={isStageReadOnly} control={<Checkbox checked={Boolean(formState.coffinConfirmed)} onChange={(e) => updateField("coffinConfirmed", e.target.checked)} />} label="Coffin confirmed" />
                  </Box>
                )}

                {stageKey === "DELIVERY" && (
                  <Box>
                    <SecLabel text="Delivery checklist" />
                    <FormControlLabel disabled={isStageReadOnly} control={<Checkbox checked={Boolean(formState.deliveryClothesChecked)} onChange={(e) => updateField("deliveryClothesChecked", e.target.checked)} />} label="Clothes checked" />
                    <FormControlLabel disabled={isStageReadOnly} control={<Checkbox checked={Boolean(formState.deliveryJewelleryChecked)} onChange={(e) => updateField("deliveryJewelleryChecked", e.target.checked)} />} label="Jewellery checked" />
                    <FormControlLabel disabled={isStageReadOnly} control={<Checkbox checked={Boolean(formState.deliveryCellphoneChecked)} onChange={(e) => updateField("deliveryCellphoneChecked", e.target.checked)} />} label="Cellphone checked" />
                    <TextField size="small" fullWidth label="Undertaker Name" value={formState.deliveryUndertakerName || ""} onChange={(e) => updateField("deliveryUndertakerName", e.target.value)} InputProps={isStageReadOnly ? { readOnly: true } : undefined} sx={{ ...intakeSx, mt: 1.5 }} />
                  </Box>
                )}

              </>
            )}

            {/* ══════════════════════════════════════════════════
                INTERNAL TRANSFER FORM
            ══════════════════════════════════════════════════ */}
            {dialogMode === "TEMPERATURE_LOG" && (
              <Box>
                <SecLabel text="AM / PM temperature log" />
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.5 }}>
                  <TextField size="small" label="Mortuary" value={formState.mortuaryName || ""} onChange={(e) => updateField("mortuaryName", e.target.value)} sx={intakeSx} />
                  <TextField size="small" label="Fridge number" value={formState.fridgeNumber || ""} onChange={(e) => updateField("fridgeNumber", e.target.value)} sx={intakeSx} />
                  <TextField size="small" label="Reading date" type="date" InputLabelProps={{ shrink: true }} value={formState.readingDate || ""} onChange={(e) => updateField("readingDate", e.target.value)} sx={intakeSx} />
                  <TextField size="small" select label="Shift" value={formState.shift || "AM"} onChange={(e) => updateField("shift", e.target.value)} sx={intakeSx}>
                    <MenuItem value="AM">AM</MenuItem>
                    <MenuItem value="PM">PM</MenuItem>
                  </TextField>
                  <TextField size="small" label="Temperature (C)" type="number" value={formState.temperatureCelsius || ""} onChange={(e) => updateField("temperatureCelsius", e.target.value)} sx={intakeSx} />
                  <TextField size="small" label="Logged by" value={formState.loggedBy || ""} onChange={(e) => updateField("loggedBy", e.target.value)} sx={intakeSx} />
                  <TextField size="small" label="Normal min (C)" type="number" value={formState.normalMinCelsius ?? -5} onChange={(e) => updateField("normalMinCelsius", e.target.value)} sx={intakeSx} />
                  <TextField size="small" label="Normal max (C)" type="number" value={formState.normalMaxCelsius ?? 5} onChange={(e) => updateField("normalMaxCelsius", e.target.value)} sx={intakeSx} />
                  <TextField size="small" fullWidth multiline rows={3} label="Corrective action for breach" value={formState.correctiveAction || ""} onChange={(e) => updateField("correctiveAction", e.target.value)} sx={{ ...intakeSx, gridColumn: "1 / -1" }} />
                </Box>
              </Box>
            )}

            {dialogMode === "STORAGE_UNIT" && (
              <Box>
                <SecLabel text="Shelf setup" />
                <Typography fontSize={12.25} color={ifasColors.muted} mb={1.25}>
                  Add one shelf at a time for the selected mortuary. These shelves become selectable during intake.
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.5 }}>
                  <TextField size="small" select label="Mortuary" value={formState.mortuaryName || ""} onChange={(e) => updateField("mortuaryName", e.target.value)} sx={{ ...intakeSx, gridColumn: "1 / -1" }} helperText="Choose the mortuary where this physical shelf exists.">
                    <MenuItem value="">Select mortuary</MenuItem>
                    {storageMortuaryOptions.map((mortuaryName) => (
                      <MenuItem key={mortuaryName} value={mortuaryName}>{mortuaryName}</MenuItem>
                    ))}
                  </TextField>
                  <TextField size="small" label="Fridge number" placeholder="e.g. FRIDGE-1" value={formState.fridgeNumber || ""} onChange={(e) => updateField("fridgeNumber", e.target.value)} sx={intakeSx} />
                  <TextField size="small" label="Shelf number" placeholder="e.g. A1" value={formState.shelfNumber || ""} onChange={(e) => updateField("shelfNumber", e.target.value)} sx={intakeSx} />
                  <TextField size="small" select label="Status" value={formState.status || "AVAILABLE"} onChange={(e) => updateField("status", e.target.value)} sx={intakeSx}>
                    <MenuItem value="AVAILABLE">Available</MenuItem>
                    <MenuItem value="OUT_OF_SERVICE">Out of Service</MenuItem>
                  </TextField>
                  <TextField size="small" fullWidth multiline rows={3} label="Notes" value={formState.notes || ""} onChange={(e) => updateField("notes", e.target.value)} sx={{ ...intakeSx, gridColumn: "1 / -1" }} />
                </Box>
              </Box>
            )}

            {dialogMode === "HYGIENE_CHECKLIST" && (
              <Box>
                <SecLabel text="Hygiene and cleaning standards" />
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.2 }}>
                  <TextField size="small" label="Mortuary" value={formState.mortuaryName || ""} onChange={(e) => updateField("mortuaryName", e.target.value)} sx={intakeSx} />
                  <TextField size="small" label="Checklist date" type="date" InputLabelProps={{ shrink: true }} value={formState.checklistDate || ""} onChange={(e) => updateField("checklistDate", e.target.value)} sx={intakeSx} />
                  <TextField size="small" select label="Shift" value={formState.shift || "AM"} onChange={(e) => updateField("shift", e.target.value)} sx={intakeSx}>
                    <MenuItem value="AM">AM</MenuItem>
                    <MenuItem value="PM">PM</MenuItem>
                  </TextField>
                  <TextField size="small" label="Completed by" value={formState.completedBy || ""} onChange={(e) => updateField("completedBy", e.target.value)} sx={intakeSx} />
                  {[
                    ["protectiveClothingWorn", "Protective clothing worn"],
                    ["noEatingDrinkingSmoking", "No eating, drinking or smoking"],
                    ["flyControlApplied", "Fly control applied"],
                    ["ventilationAndLightingOk", "Ventilation and lighting ok"],
                    ["wasteBinsClosedAndEmptied", "Waste bins closed and emptied"],
                    ["instrumentsCleanDryStored", "Instruments clean, dry and stored"],
                    ["entrancesClosed", "Entrances closed"],
                    ["coldRoomAndTraysClean", "Cold room and trays clean"],
                    ["refrigerationDoorsSecure", "Refrigeration doors closed and secure"],
                    ["floorsWallsSinksDisinfected", "Floors, walls, taps, sinks disinfected"],
                    ["coffinsCasketsDusted", "Coffins and caskets dusted"],
                    ["airFreshenersUsed", "Air fresheners used"],
                    ["laundryRosterFollowed", "Laundry roster followed"],
                    ["handsWashedWithDisinfectant", "Hands washed with disinfectant"],
                    ["showerCompletedEndOfDay", "End-of-day shower completed"],
                    ["uvLightManaged", "UV light switched correctly"],
                  ].map(([key, label]) => (
                    <FormControlLabel key={key} control={<Checkbox checked={Boolean(formState[key])} onChange={(e) => updateField(key, e.target.checked)} />} label={label} />
                  ))}
                  <TextField size="small" fullWidth multiline rows={3} label="Exceptions / corrective notes" value={formState.exceptions || ""} onChange={(e) => updateField("exceptions", e.target.value)} sx={{ ...intakeSx, gridColumn: "1 / -1" }} />
                  <TextField size="small" label="Supervisor name" value={formState.supervisorName || ""} onChange={(e) => updateField("supervisorName", e.target.value)} sx={intakeSx} />
                  <SignatureUploadField
                    label="Supervisor Signature"
                    module="MORTUARY"
                    referenceNumber={selectedRecord?.bodyRemovalNumber || selectedRecord?.id}
                    fieldName="hygieneSupervisorSignature"
                    value={formState.supervisorSignature || ""}
                    onUploaded={(fileKey) => updateField("supervisorSignature", fileKey)}
                  />
                </Box>
              </Box>
            )}

            {dialogMode === "CREATE_INTERNAL_TRANSFER" && (
              <>
                <Box>
                  <SecLabel text="Case and family reference" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <TextField size="small" label="Funeral File Number" value={formState.funeralFileNumber || ""} onChange={(e) => updateField("funeralFileNumber", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Policy / Invoice Number" value={formState.policyOrInvoiceNumber || ""} onChange={(e) => updateField("policyOrInvoiceNumber", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Family Representative" value={formState.familyRepresentativeName || ""} onChange={(e) => updateField("familyRepresentativeName", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Representative ID" value={formState.familyRepresentativeIdNumber || ""} onChange={(e) => updateField("familyRepresentativeIdNumber", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Relationship To Deceased" value={formState.relationshipToDeceased || ""} onChange={(e) => updateField("relationshipToDeceased", e.target.value)} sx={intakeSx} />
                  </Box>
                </Box>
                <Box>
                  <SecLabel text="Transfer details" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <TextField size="small" label="Created By" value={formState.createdBy || ""} onChange={(e) => updateField("createdBy", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Transfer Date/Time" type="datetime-local" InputLabelProps={{ shrink: true }} value={formState.transferDateTime || ""} onChange={(e) => updateField("transferDateTime", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Departure Mortuary" value={formState.departureMortuary || ""} onChange={(e) => updateField("departureMortuary", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Receiving Mortuary" value={formState.receivingMortuary || ""} onChange={(e) => updateField("receivingMortuary", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Dispatching Personnel" value={formState.dispatchingPersonnelName || ""} onChange={(e) => updateField("dispatchingPersonnelName", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Vehicle Registration" value={formState.transferVehicleRegistration || ""} onChange={(e) => updateField("transferVehicleRegistration", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Undertaker Name" value={formState.transferUndertakerName || ""} onChange={(e) => updateField("transferUndertakerName", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Mileage Out" type="number" value={formState.mileageOut || ""} onChange={(e) => updateField("mileageOut", e.target.value)} sx={intakeSx} />
                    <TextField size="small" select label="Clothes Transferred" value={formState.clothesTransferred || "NA"} onChange={(e) => updateField("clothesTransferred", e.target.value)} sx={intakeSx}>
                      <MenuItem value="YES">Yes</MenuItem>
                      <MenuItem value="NO">No</MenuItem>
                      <MenuItem value="NA">N/A</MenuItem>
                    </TextField>
                  </Box>
                </Box>
                <Box>
                  <SecLabel text="Pre-transfer checklist" />
                  {[
                    ["familyViewedConfirmed", "Family identified body"],
                    ["clothesBelongingsHandedOver", "Clothes and belongings handed over"],
                    ["bodyInBadState", "Body in bad state"],
                    ["operationsManagerEscalated", "Operations Manager escalation required"],
                    ["bothBranchesAgreed", "Both branches agreed"],
                  ].map(([key, label]) => (
                    <FormControlLabel key={key} control={<Checkbox checked={Boolean(formState[key])} onChange={(e) => updateField(key, e.target.checked)} />} label={label} sx={{ display: "block" }} />
                  ))}
                </Box>
                <Box sx={{ border: "1px solid #FED7AA", bgcolor: "#FFF7ED", borderRadius: 2, p: 1.5 }}>
                  <Typography fontSize={12.5} fontWeight={800} color="#9A3412">
                    Receiving shelf, mileage-in, receiving supervisor signature, and Operations approval are captured later in Body Dispatch.
                  </Typography>
                </Box>
                <TextField size="small" fullWidth label="Notes" multiline rows={3} value={formState.notes || ""} onChange={(e) => updateField("notes", e.target.value)} sx={intakeSx} />
              </>
            )}

            {/* ══════════════════════════════════════════════════
                EXTERNAL RELEASE FORM
            ══════════════════════════════════════════════════ */}
            {dialogMode === "CREATE_EXTERNAL_RELEASE" && (
              <>
                <Box>
                  <SecLabel text="Case and claimant reference" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <TextField size="small" label="Funeral File Number" value={formState.funeralFileNumber || ""} onChange={(e) => updateField("funeralFileNumber", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Policy / Invoice Number" value={formState.policyOrInvoiceNumber || ""} onChange={(e) => updateField("policyOrInvoiceNumber", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Family Representative" value={formState.familyRepresentativeName || ""} onChange={(e) => updateField("familyRepresentativeName", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Representative ID" value={formState.familyRepresentativeIdNumber || ""} onChange={(e) => updateField("familyRepresentativeIdNumber", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Relationship To Deceased" value={formState.relationshipToDeceased || ""} onChange={(e) => updateField("relationshipToDeceased", e.target.value)} sx={intakeSx} />
                  </Box>
                </Box>
                <Box>
                  <SecLabel text="Release details" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <TextField size="small" label="Created By" value={formState.createdBy || ""} onChange={(e) => updateField("createdBy", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Branch Name" value={formState.branchName || ""} onChange={(e) => updateField("branchName", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Release Date/Time" type="datetime-local" InputLabelProps={{ shrink: true }} value={formState.externalReleaseDateTime || ""} onChange={(e) => updateField("externalReleaseDateTime", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="SAPFA Rate Amount" type="number" value={formState.sapfaRateAmount || ""} onChange={(e) => updateField("sapfaRateAmount", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Amount Paid" type="number" value={formState.amountPaid || ""} onChange={(e) => updateField("amountPaid", e.target.value)} sx={intakeSx} />
                  </Box>
                  <TextField size="small" fullWidth label="Reason For Taking Body" multiline rows={2} value={formState.reasonForTakingBody || ""} onChange={(e) => updateField("reasonForTakingBody", e.target.value)} sx={{ ...intakeSx, mt: 1.5 }} />
                </Box>
                <Box>
                  <SecLabel text="Parlour details" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <TextField size="small" label="Parlour Name" value={formState.parlourName || ""} onChange={(e) => updateField("parlourName", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Parlour Contact" value={formState.parlourContact || ""} onChange={(e) => updateField("parlourContact", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Driver Name" value={formState.parlourDriverName || ""} onChange={(e) => updateField("parlourDriverName", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Driver Contact" value={formState.parlourDriverContact || ""} onChange={(e) => updateField("parlourDriverContact", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Driver ID Number" value={formState.parlourDriverIdNumber || ""} onChange={(e) => updateField("parlourDriverIdNumber", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Federation Certificate Number" value={formState.parlourFederationCertificateNumber || ""} onChange={(e) => updateField("parlourFederationCertificateNumber", e.target.value)} sx={intakeSx} />
                    <TextField size="small" select label="Clothes With Body" value={formState.clothesWithBody || "NA"} onChange={(e) => updateField("clothesWithBody", e.target.value)} sx={intakeSx}>
                      <MenuItem value="YES">Yes</MenuItem>
                      <MenuItem value="NO">No</MenuItem>
                      <MenuItem value="NA">N/A</MenuItem>
                    </TextField>
                  </Box>
                </Box>
                <Box>
                  <SecLabel text="Verification checklist" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0 }}>
                    {[
                      ["familyViewedConfirmed", "Family viewed and identified body"],
                      ["deathCertificateVerified", "Death certificate verified"],
                      ["deceasedIdVerified", "Deceased ID verified"],
                      ["informantIdVerified", "Informant ID verified"],
                      ["dha1663Verified", "DHA1663 verified"],
                      ["parlourDriverIdVerified", "Parlour driver ID verified"],
                      ["federationCertificateVerified", "Federation certificate verified"],
                      ["invoiceVerified", "Invoice verified"],
                      ["proofOfPaymentVerified", "Proof of payment verified"],
                      ["financePaymentConfirmed", "Finance payment confirmed"],
                      ["branchManagerOverride", "Branch manager override"],
                      ["claimantDeclarationAccepted", "Claimant declaration accepted"],
                    ].map(([key, label]) => (
                      <FormControlLabel key={key} control={<Checkbox checked={Boolean(formState[key])} onChange={(e) => updateField(key, e.target.checked)} />} label={label} />
                    ))}
                  </Box>
                </Box>
                <Box>
                  <SecLabel text="Finance & signatures" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <TextField size="small" label="Finance Confirmed By" value={formState.financeConfirmedBy || ""} onChange={(e) => updateField("financeConfirmedBy", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Finance Confirmed At" type="datetime-local" InputLabelProps={{ shrink: true }} value={formState.financeConfirmedAt || ""} onChange={(e) => updateField("financeConfirmedAt", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Branch Manager Override By" value={formState.branchManagerOverrideBy || ""} onChange={(e) => updateField("branchManagerOverrideBy", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Override Reason" value={formState.branchManagerOverrideReason || ""} onChange={(e) => updateField("branchManagerOverrideReason", e.target.value)} sx={intakeSx} />
                    <SignatureUploadField
                      label="Parlour Representative Signature"
                      module="BODY_DISPATCH"
                      referenceNumber={selectedRecord?.bodyRemovalNumber || selectedRecord?.id}
                      fieldName="parlourRepresentativeSignature"
                      value={formState.parlourRepresentativeSignature || ""}
                      onUploaded={(fileKey) => updateField("parlourRepresentativeSignature", fileKey)}
                    />
                    <SignatureUploadField
                      label="Parlour Stamp Image"
                      module="BODY_DISPATCH"
                      referenceNumber={selectedRecord?.bodyRemovalNumber || selectedRecord?.id}
                      fieldName="parlourStampImage"
                      value={formState.parlourStampImage || ""}
                      onUploaded={(fileKey) => updateField("parlourStampImage", fileKey)}
                    />
                    <TextField size="small" label="Parlour Signature Date" type="date" InputLabelProps={{ shrink: true }} value={formState.parlourSignatureDate || ""} onChange={(e) => updateField("parlourSignatureDate", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Claimant Name" value={formState.claimantName || ""} onChange={(e) => updateField("claimantName", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Claimant ID" value={formState.claimantIdNumber || ""} onChange={(e) => updateField("claimantIdNumber", e.target.value)} sx={intakeSx} />
                    <SignatureUploadField
                      label="Dispatching Mortuary Signature"
                      module="BODY_DISPATCH"
                      referenceNumber={selectedRecord?.bodyRemovalNumber || selectedRecord?.id}
                      fieldName="dispatchingMortuarySignature"
                      value={formState.dispatchingMortuarySignature || ""}
                      onUploaded={(fileKey) => updateField("dispatchingMortuarySignature", fileKey)}
                    />
                    <TextField size="small" label="Dispatching Sign-off By" value={formState.dispatchingMortuarySignOffBy || ""} onChange={(e) => updateField("dispatchingMortuarySignOffBy", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Dispatching Sign-off Date" type="date" InputLabelProps={{ shrink: true }} value={formState.dispatchingMortuarySignOffDate || ""} onChange={(e) => updateField("dispatchingMortuarySignOffDate", e.target.value)} sx={intakeSx} />
                    <SignatureUploadField
                      label="Claimant Declaration Signature"
                      module="BODY_DISPATCH"
                      referenceNumber={selectedRecord?.bodyRemovalNumber || selectedRecord?.id}
                      fieldName="claimantDeclarationSignature"
                      value={formState.claimantDeclarationSignature || ""}
                      onUploaded={(fileKey) => updateField("claimantDeclarationSignature", fileKey)}
                    />
                    <TextField size="small" label="Claimant Declaration Date" type="date" InputLabelProps={{ shrink: true }} value={formState.claimantDeclarationDate || ""} onChange={(e) => updateField("claimantDeclarationDate", e.target.value)} sx={intakeSx} />
                  </Box>
                </Box>
              </>
            )}

            {/* ══════════════════════════════════════════════════
                FAMILY DELIVERY FORM
            ══════════════════════════════════════════════════ */}
            {dialogMode === "CREATE_FAMILY_DELIVERY" && (
              <>
                <Box>
                  <SecLabel text="Case and family reference" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <TextField size="small" label="Funeral File Number" value={formState.funeralFileNumber || ""} onChange={(e) => updateField("funeralFileNumber", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Policy / Invoice Number" value={formState.policyOrInvoiceNumber || ""} onChange={(e) => updateField("policyOrInvoiceNumber", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Family Representative" value={formState.familyRepresentativeName || ""} onChange={(e) => updateField("familyRepresentativeName", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Representative ID" value={formState.familyRepresentativeIdNumber || ""} onChange={(e) => updateField("familyRepresentativeIdNumber", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Relationship To Deceased" value={formState.relationshipToDeceased || ""} onChange={(e) => updateField("relationshipToDeceased", e.target.value)} sx={intakeSx} />
                  </Box>
                </Box>
                <Box>
                  <SecLabel text="Delivery details" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <TextField size="small" label="Created By" value={formState.createdBy || ""} onChange={(e) => updateField("createdBy", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Delivery Date/Time" type="datetime-local" InputLabelProps={{ shrink: true }} value={formState.familyDeliveryDateTime || ""} onChange={(e) => updateField("familyDeliveryDateTime", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Delivery Address" value={formState.deliveryAddress || ""} onChange={(e) => updateField("deliveryAddress", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Via Address" value={formState.viaAddress || ""} onChange={(e) => updateField("viaAddress", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Casket Type" value={formState.casketType || ""} onChange={(e) => updateField("casketType", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Casket Colour" value={formState.casketColour || ""} onChange={(e) => updateField("casketColour", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Undertaker Name" value={formState.undertakerName || ""} onChange={(e) => updateField("undertakerName", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Vehicle Registration" value={formState.vehicleRegistration || ""} onChange={(e) => updateField("vehicleRegistration", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Mileage Out" type="number" value={formState.familyDeliveryMileageOut || ""} onChange={(e) => updateField("familyDeliveryMileageOut", e.target.value)} sx={intakeSx} />
                  </Box>
                </Box>
                <Box>
                  <SecLabel text="Recipient" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <TextField size="small" label="Recipient Name" value={formState.recipientName || ""} onChange={(e) => updateField("recipientName", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Recipient ID Number" value={formState.recipientIdNumber || ""} onChange={(e) => updateField("recipientIdNumber", e.target.value)} sx={intakeSx} />
                    <TextField size="small" label="Recipient Relationship" value={formState.recipientRelationship || ""} onChange={(e) => updateField("recipientRelationship", e.target.value)} sx={intakeSx} />
                    <SignatureUploadField
                      label="Recipient Signature"
                      module="BODY_DISPATCH"
                      referenceNumber={selectedRecord?.bodyRemovalNumber || selectedRecord?.id}
                      fieldName="recipientSignature"
                      value={formState.recipientSignature || ""}
                      onUploaded={(fileKey) => updateField("recipientSignature", fileKey)}
                    />
                    <SignatureUploadField
                      label="Icebolethu Representative Signature"
                      module="BODY_DISPATCH"
                      referenceNumber={selectedRecord?.bodyRemovalNumber || selectedRecord?.id}
                      fieldName="icebolethuRepresentativeSignature"
                      value={formState.icebolethuRepresentativeSignature || ""}
                      onUploaded={(fileKey) => updateField("icebolethuRepresentativeSignature", fileKey)}
                    />
                  </Box>
                </Box>
                <Box>
                  <SecLabel text="Checklist" />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0 }}>
                    {[
                      ["clothesReturned", "Clothes returned"],
                      ["cellphoneJewelleryReturned", "Cellphone and jewellery returned"],
                      ["coffinSprayConfirmed", "Coffin spray confirmed"],
                      ["photoProgrammeConfirmed", "Photo / programme confirmed"],
                      ["crossPlaqueConfirmed", "Cross / plaque confirmed"],
                      ["originalDeathCertificateVerified", "Original death certificate verified"],
                      ["beneficiaryIdVerified", "Beneficiary ID verified"],
                      ["bodyReadinessConfirmed", "Body readiness confirmed"],
                      ["familyViewingConfirmed", "Family viewing completed"],
                      ["personalEffectsSignedOff", "Personal effects signed off"],
                      ["handlesExplained", "Casket handles explained"],
                    ].map(([key, label]) => (
                      <FormControlLabel key={key} control={<Checkbox checked={Boolean(formState[key])} onChange={(e) => updateField(key, e.target.checked)} />} label={label} />
                    ))}
                  </Box>
                </Box>
                <TextField size="small" fullWidth label="Notes" multiline rows={3} value={formState.notes || ""} onChange={(e) => updateField("notes", e.target.value)} sx={intakeSx} />
              </>
            )}

          </Box>
        </DialogContent>

        {/* ── Footer ── */}
        <DialogActions sx={{ px: 3, py: 1.9, bgcolor: "#f9fafb", borderTop: "1px solid #f1f5f9", gap: 1.25 }}>
          <Button
            onClick={resetForm}
            sx={{
              textTransform: "none",
              borderRadius: "12px",
              px: 3.25,
              py: 1,
              fontWeight: 700,
              color: "#64748b",
              border: "1px solid #e2e8f0",
              backgroundColor: "#ffffff",
              "&:hover": {
                backgroundColor: "#f8fafc",
                borderColor: "#cbd5e1",
              },
            }}
          >
            {isStageReadOnly ? "Close" : "Cancel"}
          </Button>
          {isStageReadOnly && canOperateMortuaryStage ? (
            <Button
              variant="outlined"
              onClick={handleEditStage}
              sx={{
                textTransform: "none",
                borderRadius: "12px",
                minWidth: 132,
                px: 3.25,
                py: 1,
                fontWeight: 800,
                borderColor: "#fed7aa",
                color: "#c2410c",
                backgroundColor: "#fff7ed",
                "&:hover": {
                  borderColor: "#fdba74",
                  backgroundColor: "#ffedd5",
                },
              }}
            >
              Edit
            </Button>
          ) : null}
          {!isStageReadOnly &&
            (dialogMode === "STAGE_COMPLETE" ? canOperateMortuaryStage : canApproveMortuary) ? (
            <Button
              variant="contained"
              onClick={submitDialog}
              disabled={disableDialogSubmit}
              sx={{
                textTransform: "none",
                borderRadius: "12px",
                minWidth: 168,
                px: 4,
                py: 1.15,
                fontWeight: 800,
                bgcolor: "#f97316",
                boxShadow: "0 10px 20px rgba(249,115,22,0.25)",
                "&:hover": {
                  bgcolor: "#ea580c",
                },
              }}
            >
              {saving ? "Saving..." : getDialogSubmitLabel(dialogMode, stageKey, dialogMode === "STAGE_VIEW" && !stageDialogReadOnly)}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </OperationsPageShell>
  );
}
